import React, { useState, useEffect, useRef, useCallback, forwardRef } from 'react';
import DOMPurify from 'dompurify';
import { useParams, useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import Draggable from 'react-draggable';
import useActiveReservation, { reservationHeldUntil } from '../../hooks/useActiveReservation';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Chip,
  Paper,
  Alert,
  CircularProgress,
  Breadcrumbs,
  IconButton,
  Tooltip,
  Divider,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  TablePagination,
} from '@mui/material';
import {
  Edit as EditIcon,
  Refresh as RefreshIcon,
  CameraAlt as CameraIcon,
  Explore as MountIcon,
  FilterAlt as FilterIcon,
  Adjust as FocuserIcon,
  RotateRight as RotatorIcon,
  Home as DomeIcon,
  Cloud as WeatherIcon,
  CenterFocusWeak as GuiderIcon,
  NavigateNext as NavigateNextIcon,
  Science as ObservatoryIcon,
  Security as SafetyMonitorIcon,
  Timeline as TimelineIcon,
  CalendarMonth as CalendarIcon,
  PowerSettingsNew as SwitchIcon,
  Settings as SettingsIcon,
  ToggleOn as ToggleIcon,
  WbSunny as FlatPanelIcon,
  Close as CloseIcon,
  Group as MembersIcon,
  CheckCircle as OnlineIcon,
  Cancel as OfflineIcon,
  PlayArrow as DispatchEnabledIcon,
  Pause as DispatchDisabledIcon,
  Security as SafetyIcon,
  Warning as UnsafeIcon,
  HelpOutline as SafetyUnknownIcon,
  ToggleOn as AutomationEnabledIcon,
  Tune as PersonalizeIcon,
  // Observatory phase and state icons
  RocketLaunch as StartupIcon,
  AcUnit as CooldownIcon,
  CheckCircleOutline as ReadyIcon,
  NightsStay as ShutdownIcon,
  Visibility as ObservingIcon,
  Build as MaintenanceIcon,
  WbTwilight as DuskIcon,
  Brightness5 as DawnIcon,
  Thunderstorm as WeatherUnsafeIcon,
  ArrowDropDown as ArrowDropDownIcon,
  GpsFixed as GpsFixedIcon,
  GpsOff as GpsOffIcon,
  OpenInNew as OpenInNewIcon,
  CropFree as CropFreeIcon,
  MyLocation as ReticleIcon,
  FingerprintOutlined as FingerprintIcon,
  Announcement as AnnouncementIcon,
  RemoveRedEye as ViewRecipientsIcon,
  Check as AcknowledgeIcon,
  Cancel as CancelMotdIcon,
  FileDownload as ExportIcon,
} from '@mui/icons-material';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import { apiService } from '../../services/apiService';
import { websocketService } from '../../services/websocketService';
import { astronomyService } from '../../services/astronomyService';
import { constructMinioUrl } from '../../utils/fileUtils';
import ObservatoryPersonalizationDialog from './ObservatoryPersonalizationDialog';
import GuiderChart from '../operator/GuiderChart';
import { getEventColor, formatEventType } from '../../utils/eventUtils';
import { useAuth } from '../../contexts/AuthContext';
import WeatherHistorySection from '../WeatherHistorySection';
import CreateMotdDialog from '../CreateMotdDialog';
import MotdDialog from '../MotdDialog';
import ConfirmDialog from '../ConfirmDialog';
import EquipmentStatusCard from './EquipmentStatusCard';
import ObservatorySubscriptionCard from './ObservatorySubscriptionCard';

// Convert decimal degrees to sexagesimal format
function decimalToSexagesimal(decimal, isRA = false) {
  if (decimal === null || decimal === undefined) return 'N/A';
  
  const absolute = Math.abs(decimal);
  
  if (isRA) {
    // RA: Already in hours from NINA
    const hours = absolute;
    const h = Math.floor(hours);
    const m = Math.floor((hours - h) * 60);
    const s = ((hours - h) * 60 - m) * 60;
    return `${h.toString().padStart(2, '0')}h ${m.toString().padStart(2, '0')}m ${s.toFixed(2).padStart(5, '0')}s`;
  } else {
    // Dec: Keep in degrees
    const sign = decimal < 0 ? '-' : '+';
    const d = Math.floor(absolute);
    const m = Math.floor((absolute - d) * 60);
    const s = ((absolute - d) * 60 - m) * 60;
    return `${sign}${d.toString().padStart(2, '0')}° ${m.toString().padStart(2, '0')}' ${s.toFixed(1).padStart(4, '0')}"`;
  }
}

// Format tracking mode for display
function formatTrackingMode(trackingMode, customRaRate, customDecRate) {
  if (!trackingMode) return 'Unknown';

  // If it's a custom mode, show the rates
  if (trackingMode === 'Custom' && (customRaRate !== undefined || customDecRate !== undefined)) {
    const raStr = customRaRate !== undefined ? `RA: ${customRaRate.toFixed(3)}` : '';
    const decStr = customDecRate !== undefined ? `Dec: ${customDecRate.toFixed(3)}` : '';
    const rates = [raStr, decStr].filter(s => s).join(', ');
    return `Custom (${rates})`;
  }

  // Otherwise just return the mode name
  return trackingMode;
}

// Get operational state display info (icon, label, color)
// Simplified v2 model with time-embedded states
function getOperationalStateInfo(state) {
  const states = {
    // Auto/derived states
    unknown: { icon: SafetyUnknownIcon, label: 'Unknown', color: 'default' },
    observing: { icon: ObservingIcon, label: 'Observing', color: 'success' },
    // Manual states (now implemented)
    dusk_startup: { icon: StartupIcon, label: 'Dusk Startup', color: 'info' },
    dusk_cooldown: { icon: CooldownIcon, label: 'Dusk Cooldown', color: 'info' },
    ready: { icon: ReadyIcon, label: 'Ready', color: 'success' },
    weather_hold: { icon: WeatherUnsafeIcon, label: 'Weather Hold', color: 'warning' },
    shutdown: { icon: ShutdownIcon, label: 'Shutdown', color: 'warning' },
    error: { icon: UnsafeIcon, label: 'Error', color: 'error' },
    // Future states (calibration)
    dusk_flats: { icon: FlatPanelIcon, label: 'Dusk Flats', color: 'info' },
    dusk_calibration: { icon: FlatPanelIcon, label: 'Dusk Calibration', color: 'info' },
    bad_weather_calibration: { icon: FlatPanelIcon, label: 'Bad Weather Cal', color: 'warning' },
    dawn_flats: { icon: FlatPanelIcon, label: 'Dawn Flats', color: 'info' },
    dawn_calibration: { icon: FlatPanelIcon, label: 'Dawn Calibration', color: 'info' },
    // Legacy states (for backwards compatibility)
    // Note: 'online'/'offline' are connection states, not operational states - excluded
    // 'idle' is excluded - it's a meaningless legacy state
    maintenance: { icon: MaintenanceIcon, label: 'Maintenance', color: 'warning' },
    weather_unsafe: { icon: WeatherUnsafeIcon, label: 'Weather Unsafe', color: 'error' },
    equipment_error: { icon: UnsafeIcon, label: 'Equipment Error', color: 'error' },
  };
  return states[state] || { icon: SafetyUnknownIcon, label: state || 'Unknown', color: 'default' };
}

// Get calculated session state display info (based on sun position)
function getCalculatedSessionInfo(sessionState) {
  const sessions = {
    daytime: { icon: FlatPanelIcon, label: 'Daytime', color: 'default', sx: { backgroundColor: '#ffeb3b', color: '#000' } },
    dusk: { icon: DuskIcon, label: 'Dusk', color: 'info' },
    night: { icon: ShutdownIcon, label: 'Night', color: 'primary' },
    dawn: { icon: DawnIcon, label: 'Dawn', color: 'warning' },
  };
  return sessions[sessionState] || null;
}


// EquipmentStatusCard imported from ./EquipmentStatusCard

// Draggable Paper component for dialogs
const DraggablePaper = forwardRef((props, ref) => {
  return (
    <Draggable handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
      <Paper ref={ref} {...props} />
    </Draggable>
  );
});

function ObservatoryDetailsBase({
  mode = 'admin', // 'admin' | 'user'
  enableEditMode = true,
  enableMemberManagement = true,
  backRoute = '/admin/observatories'
}) {
  const { id } = useParams();
  // Reservation covering now, if any. A reserved observatory is not available for
  // scheduling no matter what dispatch_enabled says, so the chip below and the
  // banner both derive from this rather than from the observatory record.
  const activeReservation = useActiveReservation(id);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [observatory, setObservatory] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [telemetryData, setTelemetryData] = useState(null);
  // Per-device toggle: when a device is disconnected the user can reveal its
  // last-reported (snapshot) values. Keyed by device key, e.g. { mount: true }.
  const [lastReportedShown, setLastReportedShown] = useState({});
  const [lastUpdate, setLastUpdate] = useState(null);
  const [astronomicalData, setAstronomicalData] = useState(null);
  const [isDataStale, setIsDataStale] = useState(false);
  const [currentObservation, setCurrentObservation] = useState(null);
  const [latestImage, setLatestImage] = useState(null);
  const [, forceUpdate] = useState(0); // For forcing re-renders
  const [ninaOptionsMenuAnchor, setNinaOptionsMenuAnchor] = useState(null);
  const [ninaOptionsDialogOpen, setNinaOptionsDialogOpen] = useState(false);
  const [ninaOptionsDialogType, setNinaOptionsDialogType] = useState(null); // 'general', 'equipment', 'autofocus', 'dome', 'imaging', 'platesolving'
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedHistoryEvent, setSelectedHistoryEvent] = useState(null);
  const [recentHistory, setRecentHistory] = useState([]); // Separate state for history data
  const [totalHistoryCount, setTotalHistoryCount] = useState(0); // Total history events count
  // Pagination for the embedded Observatory History tab. Mirrors the embedded
  // observation event log (ObservationLog.js): events are sliced into pages,
  // default 25 rows/page. Only the most-recent HISTORY_BATCH_SIZE events are
  // loaded up front; additional batches are pulled lazily as the user pages
  // beyond what's loaded.
  const [historyPage, setHistoryPage] = useState(0);
  const [historyRowsPerPage, setHistoryRowsPerPage] = useState(25);
  const [loadingMoreHistory, setLoadingMoreHistory] = useState(false);
  const [exportingHistory, setExportingHistory] = useState(false);
  const [accessLevelData, setAccessLevelData] = useState(null); // Access level + section visibility
  const [personalizationDialogOpen, setPersonalizationDialogOpen] = useState(false);
  const [previewRole, setPreviewRole] = useState(null); // null = normal view, 'owner'/'admin'/'user' = preview
  const [observatoryTabIndex, setObservatoryTabIndex] = useState(0);
  const [createMotdOpen, setCreateMotdOpen] = useState(false);
  const [activeMotds, setActiveMotds] = useState([]);
  const [allMotds, setAllMotds] = useState([]);
  const [selectedMotd, setSelectedMotd] = useState(null);
  const [motdDialogOpen, setMotdDialogOpen] = useState(false);
  const [motdRecipientsOpen, setMotdRecipientsOpen] = useState(false);
  const [motdRecipientsData, setMotdRecipientsData] = useState(null);
  const [motdRecipientsLoading, setMotdRecipientsLoading] = useState(false);
  const [cancelMotdOpen, setCancelMotdOpen] = useState(false);
  const [cancelMotdTarget, setCancelMotdTarget] = useState(null);
  const [cancelMotdReason, setCancelMotdReason] = useState('');

  // Auto-open Customize or MOTD dialogs when navigated here with ?action=...
  const [searchParams, setSearchParams] = useSearchParams();
  // Floating-window scoped launches (Admin Control Center → Observatory
  // MOTD action) include standalone=1; we render only the requested
  // dialog instead of the full Observatory Details page.
  const isStandalone = searchParams.get('standalone') === '1';
  const closeFloatingWindow = () => {
    if (isStandalone && typeof window !== 'undefined' && window.parent !== window) {
      try { window.parent.postMessage({ type: 'closeFloatingWindow' }, '*'); } catch (_) { /* */ }
    }
  };
  useEffect(() => {
    const action = searchParams.get('action');
    if (!action) return;
    // Wait for the observatory record to load before opening the
    // dialog. ObservatoryPersonalizationDialog initialises its
    // `visibility` state from `observatory` exactly once when it
    // transitions to open; if we open while observatory is still
    // null, the init useEffect early-returns and the dialog renders
    // a blank body (because of `if (!visibility) return null`).
    if (!observatory) return;
    if (action === 'customize') setPersonalizationDialogOpen(true);
    else if (action === 'motd') setCreateMotdOpen(true);
    if (action === 'customize' || action === 'motd') {
      const next = new URLSearchParams(searchParams);
      next.delete('action');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, observatory]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadObservatory(true); // Initial load with loading spinner
    loadRecentHistory(); // Load history separately
    loadAccessLevel(); // Load access level and section visibility
    loadActiveMotds(); // Load active MOTDs
    // MOTD history loads in a manage-gated effect below (server 403s non-managers).

    // Set up refresh every 30 seconds (background updates, no spinner)
    // History is now updated via WebSocket subscription to observation_updated events
    const refreshInterval = setInterval(() => {
      loadObservatory(false); // Background refresh without loading spinner
    }, 30000);

    return () => clearInterval(refreshInterval);
  }, []);

  // MOTD history (all messages, incl. expired) is a manager-only endpoint — the server returns 403
  // for non-managers, which also logs an "access denied" audit entry. Only fetch it once the access
  // level is known AND the viewer can manage this observatory (owner / manage / admin / server_admin).
  useEffect(() => {
    const canManage = user?.role === 'server_admin' || (accessLevelData && (
      accessLevelData.is_owner ||
      accessLevelData.access_level === 'own' ||
      accessLevelData.access_level === 'manage' ||
      accessLevelData.is_admin
    ));
    if (canManage) loadMotdHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessLevelData, user]);

  // Update time remaining every second if there's an active observation
  useEffect(() => {
    if (currentObservation &&
        ['assigned', 'in_progress'].includes(currentObservation.status) &&
        currentObservation.utc_end_time) {
      const timer = setInterval(() => {
        forceUpdate(prev => prev + 1);
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [currentObservation]);

  // Planetarium: iframe ref + observation markers
  const planetariumIframeRef = useRef(null);
  const planetariumReadyRef = useRef(false);
  const [planetariumMarkers, setPlanetariumMarkers] = useState([]);
  const [trackMount, setTrackMount] = useState(true);
  const [overlayMode, setOverlayMode] = useState(null); // null = use observatory default, 'reticle', 'fov'
  const broadcastChannelRef = useRef(null);
  const observationDataRef = useRef([]);

  // Track if we need to reload observatory data when it comes online
  const [needsReload, setNeedsReload] = useState(false);

  // Rolling RMS history for the live guider plot (issue #673). Mirrors the
  // ObservatoryProvider's guiderHistory but maintained locally because this
  // page is rendered outside the operator layout's provider scope.
  const [guiderHistory, setGuiderHistory] = useState([]);

  useEffect(() => {
    // Set up WebSocket listener for telemetry updates
    const handleTelemetryUpdate = (data) => {
      if (data.observatory_id === id) {
        // If observatory was offline and we're receiving telemetry, it's back online
        // Flag that we need to reload to get updated plugin_options
        setObservatory(prevObservatory => {
          if (prevObservatory && prevObservatory.status === 'offline') {
            setNeedsReload(true);
          }
          return prevObservatory;
        });

        // Only update if we have valid data - merge with existing state to prevent flickering
        setTelemetryData(prevData => {
          const newData = { ...prevData };

          // Only update equipment_status if new data is provided
          if (data.equipment_status) {
            newData.equipment_status = {
              ...prevData?.equipment_status,
              ...data.equipment_status
            };
          }

          // Only update system_status if new data is provided - MERGE to preserve operating_system info
          if (data.system_status) {
            newData.system_status = {
              ...prevData?.system_status,
              ...data.system_status
            };
          }

          return newData;
        });

        // Guider state arrives on its own channel — see handleGuiderUpdate below.
        // telemetry_update payloads no longer carry equipment_status.guider.

        // Update observatory state with live operations flags so banners/chips react immediately
        if (data.system_status) {
          setObservatory(prev => {
            if (!prev) return prev;
            const updated = { ...prev };
            if (data.system_status.operations_enabled !== undefined) {
              updated.system_status = {
                ...prev.system_status,
                operations_enabled: data.system_status.operations_enabled,
              };
              updated.plugin_options = {
                ...prev.plugin_options,
                operations_enabled: data.system_status.operations_enabled,
              };
            }
            if (data.system_status.respect_dark_time_constraints !== undefined) {
              updated.plugin_options = {
                ...updated.plugin_options,
                respect_dark_time_constraints: data.system_status.respect_dark_time_constraints,
              };
            }
            return updated;
          });
        }

        setLastUpdate(new Date());
        setIsDataStale(false);
      }
    };

    // Set up WebSocket listener for observation updates
    const handleObservationUpdate = async (data) => {
      // Check if this observation is assigned to our observatory
      if (data.assigned_to === id || data.observatory_id === id) {
        // Reload current observation when status changes
        if (data.status === 'in_progress' || data.status === 'assigned') {
          setCurrentObservation(data);
        } else if (data.status === 'completed' || data.status === 'failed' || data.status === 'aborted') {
          // Clear current observation when it's no longer active
          try {
            const currentObs = await apiService.getObservatoryCurrentObservation(id);
            setCurrentObservation(currentObs);
          } catch (err) {
            console.error('Error reloading current observation:', err);
          }

          // When an observation completes, reload the latest image
          if (data.status === 'completed') {
            try {
              const image = await apiService.getObservatoryLatestImage(id);
              setLatestImage(image);
            } catch (err) {
              console.error('Error reloading latest image:', err);
            }
          }
        }
        // Reload history when observation status changes
        loadRecentHistory();
      }
    };

    // Guider arrives on its own message type (issue #673 follow-up) so it doesn't
    // overload telemetry_update's "full equipment snapshot" contract. Merge the
    // subdoc into telemetryData.equipment_status so consumers reading
    // telemetryData.equipment_status.guider still work, and accumulate RMS
    // history while actively guiding.
    const handleGuiderUpdate = (data) => {
      if (data.observatory_id !== id) return;
      const guider = data.guider;
      if (!guider) return;

      setTelemetryData(prev => ({
        ...prev,
        equipment_status: {
          ...prev?.equipment_status,
          guider,
        },
      }));

      if (guider.connected && guider.guiding && guider.rms_error != null) {
        setGuiderHistory(prev => [
          ...prev.slice(-59),
          {
            time: new Date(data.timestamp || Date.now()).toLocaleTimeString('en-US', { hour12: false }),
            rms: guider.rms_error,
            ra_rms: guider.ra_rms || 0,
            dec_rms: guider.dec_rms || 0,
          },
        ]);
      }
    };

    websocketService.onTelemetryUpdate(handleTelemetryUpdate);
    websocketService.onGuiderUpdate(handleGuiderUpdate);
    websocketService.on('observation_updated', handleObservationUpdate);

    return () => {
      websocketService.offTelemetryUpdate(handleTelemetryUpdate);
      websocketService.offGuiderUpdate(handleGuiderUpdate);
      websocketService.off('observation_updated', handleObservationUpdate);
    };
  }, [id]);
  
  // Check for stale data every second
  useEffect(() => {
    const checkStaleData = setInterval(() => {
      if (lastUpdate) {
        const timeSinceUpdate = Date.now() - lastUpdate.getTime();
        // Consider data stale if no update for 60 seconds
        setIsDataStale(timeSinceUpdate > 60000);
      }
    }, 1000);
    
    return () => clearInterval(checkStaleData);
  }, [lastUpdate]);

  // Separate useEffect for client-side astronomical calculations
  useEffect(() => {
    if (!observatory?.location?.latitude || !observatory?.location?.longitude) {
      return;
    }

    // Start client-side astronomical calculations
    const cleanup = astronomyService.startRealTimeUpdates(observatory, (data) => {
      setAstronomicalData(data);
    });

    return cleanup;
  }, [observatory]);


  // Number of history events fetched per request. The first batch loads the
  // most-recent HISTORY_BATCH_SIZE; further batches are appended on demand.
  const HISTORY_BATCH_SIZE = 100;

  const loadRecentHistory = async () => {
    // Load recent history from separate API (ObservatoryHistory collection).
    // Only the most-recent batch is loaded up front; older events are pulled
    // lazily by loadMoreHistory() as the user pages past what's loaded.
    try {
      const historyResponse = await apiService.getObservatoryHistory(id, { page: 1, limit: HISTORY_BATCH_SIZE });

      // Handle both response formats: data.events or data.history
      const events = historyResponse.data?.events || historyResponse.data?.history || [];
      const total = historyResponse.data?.pagination?.total_events || historyResponse.data?.summary?.total_events || historyResponse.data?.total || events.length;
      setRecentHistory(events);
      setTotalHistoryCount(total);

    } catch (historyError) {
      console.error('Failed to load history:', historyError.message);
      setRecentHistory([]); // Set empty array if history loading fails
    }
  };

  // Pull the next batch of older history events and append them. Triggered
  // when the user pages beyond the rows already loaded.
  const loadMoreHistory = async () => {
    if (loadingMoreHistory) return;
    if (recentHistory.length >= totalHistoryCount) return; // everything loaded
    setLoadingMoreHistory(true);
    try {
      // Events are returned newest-first; the next server page continues the
      // sequence after what we already hold.
      const nextPage = Math.floor(recentHistory.length / HISTORY_BATCH_SIZE) + 1;
      const resp = await apiService.getObservatoryHistory(id, { page: nextPage, limit: HISTORY_BATCH_SIZE });
      const events = resp.data?.events || resp.data?.history || [];
      const total = resp.data?.pagination?.total_events || resp.data?.summary?.total_events || resp.data?.total || totalHistoryCount;
      setRecentHistory(prev => [...prev, ...events]);
      setTotalHistoryCount(total);
    } catch (err) {
      console.error('Failed to load more history:', err.message);
    } finally {
      setLoadingMoreHistory(false);
    }
  };

  // Export the observatory's full history as CSV. Fetches all events (not just
  // the lazily-loaded set) so the export is complete.
  const handleExportHistoryCsv = async () => {
    setExportingHistory(true);
    try {
      const resp = await apiService.getObservatoryHistory(id, { page: 1, limit: 10000 });
      const events = resp.data?.events || resp.data?.history || [];
      const cols = ['Timestamp (UTC)', 'Event Type', 'Source', 'Description'];
      const esc = (v) => {
        const s = String(v ?? '');
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const rows = events.map(e => [
        e.timestamp ? new Date(e.timestamp).toISOString() : '',
        e.event_type || '',
        e.source || '',
        e.description || '',
      ]);
      const csv = [cols.join(','), ...rows.map(r => r.map(esc).join(','))].join('\n') + '\n';
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `observatory-${id}-history-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('History CSV export failed:', err.message);
    } finally {
      setExportingHistory(false);
    }
  };

  // When the user pages past the rows we've loaded, pull the next batch. This
  // only fires off interactions with the history pagination, so no tab guard
  // is needed. Cascades (re-runs as recentHistory grows) until the requested
  // page is covered or everything is loaded.
  useEffect(() => {
    const neededRows = (historyPage + 1) * historyRowsPerPage;
    if (neededRows > recentHistory.length && recentHistory.length < totalHistoryCount && !loadingMoreHistory) {
      loadMoreHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyPage, historyRowsPerPage, recentHistory.length, totalHistoryCount, loadingMoreHistory]);

  const loadActiveMotds = async () => {
    try {
      const response = await apiService.getActiveMotds(id);
      setActiveMotds(response.data || []);
    } catch (err) {
      console.error('Failed to load MOTDs:', err);
    }
  };

  const handleMotdAcknowledged = (motdId) => {
    setActiveMotds(prev => prev.filter(m => m._id !== motdId));
  };

  const loadMotdHistory = async () => {
    try {
      const response = await apiService.getMotdHistory(id);
      setAllMotds(response.data || []);
    } catch (err) {
      console.error('Failed to load MOTD history:', err);
    }
  };

  const handleAcknowledgeMotdFromTable = async (motdId) => {
    try {
      await apiService.acknowledgeMotd(id, motdId);
      loadActiveMotds();
      loadMotdHistory();
    } catch (err) {
      console.error('Failed to acknowledge MOTD:', err);
    }
  };

  const handleCancelMotdConfirm = async () => {
    if (!cancelMotdTarget || !cancelMotdReason.trim()) return;
    try {
      await apiService.deactivateMotd(id, cancelMotdTarget._id, cancelMotdReason.trim());
      setCancelMotdOpen(false);
      setCancelMotdTarget(null);
      setCancelMotdReason('');
      loadActiveMotds();
      loadMotdHistory();
    } catch (err) {
      console.error('Failed to cancel MOTD:', err);
    }
  };

  const handleViewMotdRecipients = async (motdId) => {
    setMotdRecipientsLoading(true);
    setMotdRecipientsOpen(true);
    try {
      const result = await apiService.getObservatoryMotdRecipients(id, motdId);
      setMotdRecipientsData(result.data);
    } catch (err) {
      console.error('Failed to load MOTD recipients:', err);
      setMotdRecipientsOpen(false);
    } finally {
      setMotdRecipientsLoading(false);
    }
  };

  const loadAccessLevel = async () => {
    try {
      const response = await apiService.getObservatoryAccessLevel(id);
      setAccessLevelData(response.data);
    } catch (error) {
      console.error('Failed to load access level:', error);
    }
  };

  // Determine user's role tier for section visibility
  const getRoleTier = () => {
    if (previewRole) return previewRole; // Preview mode overrides actual role
    if (!accessLevelData) return 'user';
    if (accessLevelData.is_owner || accessLevelData.access_level === 'own') return 'owner';
    if (accessLevelData.access_level === 'manage') return 'admin';
    return 'user';
  };

  // Check if a section should be visible for the current user
  // Server admins always see the default view (bypass personalization) unless previewing
  const isSectionVisible = (sectionKey, defaultVisible = true) => {
    if (user?.role === 'server_admin' && !previewRole) return true; // Server admin sees defaults
    const visibility = accessLevelData?.page_section_visibility;
    // No saved setting -> fall back to the section's default, which MUST match
    // the personalization dialog's DEFAULT_VISIBILITY. Opt-in sections like
    // planetarium_view / weather_history default to hidden; everything else
    // defaults to visible.
    if (!visibility || !visibility[sectionKey]) return defaultVisible;
    const roleTier = getRoleTier();
    return visibility[sectionKey][roleTier] !== false;
  };

  // Map visibility device keys (snake_case) to equipment data keys (camelCase)
  const equipmentDataKeyMap = {
    mount: 'mount', camera: 'camera', filter_wheel: 'filterWheel',
    focuser: 'focuser', rotator: 'rotator', dome: 'dome',
    weather_station: 'weather', safety_monitor: 'safetyMonitor',
    guider: 'guider', switch_hub: 'switches', flat_device: 'flatDevice',
  };

  // Check if an individual equipment device should be visible
  const isEquipmentVisible = (deviceKey) => {
    if (user?.role === 'server_admin' && !previewRole) return true;
    const visibility = accessLevelData?.page_section_visibility;
    if (visibility?.equipment?.[deviceKey]) {
      const roleTier = getRoleTier();
      if (visibility.equipment[deviceKey][roleTier] === false) return false;
    }
    // Hide disconnected equipment if behavior setting is enabled for the current role
    const hideDisc = accessLevelData?.page_behavior?.hide_disconnected_equipment;
    if (hideDisc && hideDisc[getRoleTier()]) {
      const dataKey = equipmentDataKeyMap[deviceKey] || deviceKey;
      if (!equipment[dataKey]?.connected) return false;
    }
    return true;
  };

  // Check if any equipment device is visible (used to hide entire section)
  const isAnyEquipmentVisible = () => {
    const devices = ['mount', 'camera', 'filter_wheel', 'focuser', 'rotator', 'dome', 'weather_station', 'safety_monitor', 'guider', 'switch_hub', 'flat_device'];
    return devices.some(d => isEquipmentVisible(d));
  };

  // Check if the user can personalize (owner, admin, or server_admin)
  const canPersonalize = user?.role === 'server_admin' || (accessLevelData && (
    accessLevelData.is_owner ||
    accessLevelData.access_level === 'own' ||
    accessLevelData.access_level === 'manage' ||
    accessLevelData.is_admin
  ));

  const handleHistoryEventClick = (event) => {
    setSelectedHistoryEvent(event);
    setHistoryModalOpen(true);
  };

  const handleHistoryModalClose = () => {
    setHistoryModalOpen(false);
    setSelectedHistoryEvent(null);
  };

  const handleResetFingerprint = async () => {
    if (!observatory) return;
    const observatoryId = observatory._id;
    setConfirm({
      title: 'Reset hardware fingerprint?',
      message: (
        <>
          <Typography variant="body2">Reset hardware fingerprint for {observatory.name}?</Typography>
          <Typography variant="body2" sx={{ mt: 1.5 }}>
            The next plugin connection will enroll the new device.
          </Typography>
        </>
      ),
      confirmLabel: 'Reset fingerprint',
      confirmColor: 'error',
      confirmIcon: <FingerprintIcon />,
      icon: <FingerprintIcon color="error" />,
      onConfirm: async () => {
        try {
          await apiService.post(`/v1/observatories/${observatoryId}/reset-fingerprint`);
          alert('Hardware fingerprint reset. Plugin can now reconnect.');
          loadObservatory(false);
        } catch (error) {
          console.error('Error resetting fingerprint:', error);
          alert('Error resetting fingerprint');
          throw error;
        }
      },
    });
  };

  const loadObservatory = async (isInitialLoad = false) => {
    // Only show loading spinner on initial page load, not on background refreshes
    if (isInitialLoad) {
      setLoading(true);
    }
    try {
      const response = await apiService.getObservatory(id);
      setObservatory(response.data);

      // If observatory is offline, clear equipment status to avoid showing stale data
      // but preserve system_status (OS info, etc.)
      if (response.data.status === 'offline') {
        setTelemetryData(prevData => ({
          ...prevData,
          equipment_status: {}
        }));
        setLastUpdate(null);
      }
      // Only update telemetry if we have valid data - merge with existing state to prevent flickering
      else if (response.data.equipment_status || response.data.system_status) {
        setTelemetryData(prevData => {
          const newData = { ...prevData };

          // Only update equipment_status if new data is provided
          if (response.data.equipment_status) {
            newData.equipment_status = {
              ...prevData?.equipment_status,
              ...response.data.equipment_status
            };
          }

          // Only update system_status if new data is provided - MERGE to preserve operating_system info
          if (response.data.system_status) {
            newData.system_status = {
              ...prevData?.system_status,
              ...response.data.system_status
            };
          }

          // Per-device last-connected snapshots (used to show "last seen"
          // values for a currently-disconnected device). Only the REST
          // payload carries this; telemetry_update broadcasts do not, so
          // fall back to the previous value rather than clobbering it.
          if (response.data.last_connected) {
            newData.last_connected = response.data.last_connected;
          }

          return newData;
        });
        setLastUpdate(response.data.last_telemetry_update ? new Date(response.data.last_telemetry_update) : null);
      }

      // Load current observation (independent - don't let failure block latest image)
      try {
        const currentObs = await apiService.getObservatoryCurrentObservation(id);
        setCurrentObservation(currentObs);
      } catch (err) {
        console.error('Error loading current observation:', err);
      }

      // Load latest image (independent - don't let failure block other data)
      try {
        const image = await apiService.getObservatoryLatestImage(id);
        setLatestImage(image);
      } catch (err) {
        console.error('Error loading latest image:', err);
      }

    } catch (error) {
      console.error('[DEBUG] Error loading observatory:', error);
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      }
    }
  };

  // Planetarium: send pointers to VirtualSky iframe via postMessage
  const sendPointersToIframe = useCallback((pointers) => {
    const iframe = planetariumIframeRef.current;
    if (iframe?.contentWindow) {
      iframe.contentWindow.postMessage({ type: 'set-pointers', pointers }, '*');
    }
  }, []);

  // Planetarium: listen for "virtualsky-ready" from iframe, then fetch observations and send markers
  useEffect(() => {
    if (!observatory?._id) return;

    const handleMessage = (event) => {
      if (event.data?.type === 'virtualsky-ready') {
        planetariumReadyRef.current = true;
        // Send any already-fetched markers and observations
        if (planetariumMarkers.length > 0) {
          sendPointersToIframe(planetariumMarkers);
        }
        if (observationDataRef.current?.length > 0) {
          const iframe = planetariumIframeRef.current;
          if (iframe?.contentWindow) {
            iframe.contentWindow.postMessage({ type: 'set-observations', observations: observationDataRef.current }, '*');
          }
        }
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [observatory?._id, planetariumMarkers, sendPointersToIframe]);

  // Planetarium: fetch recent observations for markers when observatory loads or changes
  useEffect(() => {
    if (!observatory?._id) return;
    const provider = observatory.branding?.planetarium_provider || 'virtualsky';
    if (provider !== 'virtualsky') return;

    const markerHours = observatory.branding?.planetarium_marker_hours || 24;
    const cutoff = new Date(Date.now() - markerHours * 60 * 60 * 1000).toISOString();

    const fetchMarkers = async () => {
      try {
        const result = await apiService.getObservations({
          observatory_id: observatory._id,
          limit: 100,
        });
        const observations = result?.data?.observations || result?.data || [];
        const filtered = observations.filter(obs => {
          const status = obs.status;
          if (!['pending', 'assigned', 'in_progress', 'completing', 'completed'].includes(status)) return false;
          const ts = obs.updated_at || obs.created_at;
          return ts && ts >= cutoff;
        });

        // Build individual entries
        const entries = filtered.map(obs => {
          const colour = obs.status === 'completed' || obs.status === 'completed_series' ? 'rgb(76,175,80)'
            : obs.status === 'in_progress' || obs.status === 'assigned' ? 'rgb(25,118,210)'
            : obs.status === 'pending' || obs.status === 'waiting_next' ? 'rgb(2,136,209)'
            : obs.status === 'failed' || obs.status === 'aborted' || obs.status === 'expired' ? 'rgb(211,47,47)'
            : obs.status === 'cancelled' || obs.status === 'paused' ? 'rgb(237,108,2)'
            : 'rgb(189,189,189)';
          const raDeg = obs.targets?.[0]?.ra || 0; // RA stored in degrees in database
          const decDeg = obs.targets?.[0]?.dec || 0;
          const expConfigs = obs.targets?.[0]?.exposure_configs || obs.exposure_configs || [];
          const filters = expConfigs.map(e => e.filter).filter(Boolean).filter((f, i, a) => a.indexOf(f) === i).join(', ') || 'N/A';
          const expInfo = expConfigs.length > 0
            ? `${expConfigs[0].exposure_time}s x ${expConfigs.reduce((sum, e) => sum + (e.exposure_count || e.count || 1), 0)}`
            : 'N/A';
          const user = typeof obs.created_by === 'object' && obs.created_by ? obs.created_by : null;
          const display = obs.created_by_display;
          const userName = user
            ? (user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.username || user.email || 'N/A')
            : display
            ? (display.full_name || display.username || 'N/A')
            : 'N/A';
          const capturedAt = obs.updated_at ? new Date(obs.updated_at).toISOString().replace('T', ' ').slice(0, 16) + ' UTC' : 'N/A';
          const obsUrl = `/observations/${obs._id || obs.id}`;
          const html = `<a href="${obsUrl}" target="_top" style="color:inherit;text-decoration:none;display:block;cursor:pointer;padding:4px 6px;border-radius:4px;transition:background 0.15s" onmouseover="this.style.background='rgba(100,180,255,0.35)'" onmouseout="this.style.background='none'">`
            + `<b>${obs.targets?.[0]?.name || 'Unknown'}</b><br>`
            + `User: ${userName}<br>`
            + `Filters: ${filters}<br>`
            + `Exposure: ${expInfo}<br>`
            + `Status: ${obs.status.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}<br>`
            + `Captured: ${capturedAt}`
            + `</a>`;
          return { ra: raDeg, dec: decDeg, label: obs.targets?.[0]?.name || '', colour, html };
        });

        // Group nearby entries (within 2 degrees) into single pointers
        const PROXIMITY_DEG = 2;
        const groups = [];
        const used = new Set();
        for (let i = 0; i < entries.length; i++) {
          if (used.has(i)) continue;
          const group = [entries[i]];
          used.add(i);
          for (let j = i + 1; j < entries.length; j++) {
            if (used.has(j)) continue;
            if (Math.abs(entries[i].ra - entries[j].ra) < PROXIMITY_DEG &&
                Math.abs(entries[i].dec - entries[j].dec) < PROXIMITY_DEG) {
              group.push(entries[j]);
              used.add(j);
            }
          }
          groups.push(group);
        }

        const pointers = groups.map(group => {
          // Use first entry's position; pick most active colour (in_progress > error > assigned > pending > completed)
          const hasInProgress = group.some(e => e.colour === 'rgb(25,118,210)');
          const hasError = group.some(e => e.colour === 'rgb(211,47,47)');
          const hasPending = group.some(e => e.colour === 'rgb(2,136,209)');
          const colour = hasInProgress ? 'rgb(25,118,210)' : hasError ? 'rgb(211,47,47)' : hasPending ? 'rgb(2,136,209)' : group[0].colour;
          const label = group.length === 1 ? group[0].label : `${group[0].label} +${group.length - 1}`;
          const separator = '<hr style="border:0;border-top:1px solid rgba(255,255,255,0.3);margin:4px 0">';
          const combinedHtml = `<div style="font-family:sans-serif;font-size:12px;line-height:1.5;max-width:240px;padding:4px;max-height:300px;overflow-y:auto">`
            + group.map(e => e.html).join(separator)
            + `</div>`;
          return { ra: group[0].ra, dec: group[0].dec, label, colour, html: combinedHtml };
        });

        // Build observation data for standalone panel
        const observationData = filtered.map(obs => {
          const colour = obs.status === 'completed' || obs.status === 'completed_series' ? 'rgb(76,175,80)'
            : obs.status === 'in_progress' || obs.status === 'assigned' ? 'rgb(25,118,210)'
            : obs.status === 'pending' || obs.status === 'waiting_next' ? 'rgb(2,136,209)'
            : obs.status === 'failed' || obs.status === 'aborted' || obs.status === 'expired' ? 'rgb(211,47,47)'
            : obs.status === 'cancelled' || obs.status === 'paused' ? 'rgb(237,108,2)'
            : 'rgb(189,189,189)';
          const expConfigs = obs.targets?.[0]?.exposure_configs || obs.exposure_configs || [];
          const filters = expConfigs.map(e => e.filter).filter(Boolean).filter((f, i, a) => a.indexOf(f) === i).join(', ') || 'N/A';
          const expInfo = expConfigs.length > 0
            ? `${expConfigs[0].exposure_time}s x ${expConfigs.reduce((sum, e) => sum + (e.exposure_count || e.count || 1), 0)}`
            : 'N/A';
          const user = typeof obs.created_by === 'object' && obs.created_by ? obs.created_by : null;
          const display = obs.created_by_display;
          const userName = user
            ? (user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.username || user.email || 'N/A')
            : display
            ? (display.full_name || display.username || 'N/A')
            : 'N/A';
          const capturedAt = obs.updated_at ? new Date(obs.updated_at).toISOString().replace('T', ' ').slice(0, 16) + ' UTC' : 'N/A';
          return {
            name: obs.targets?.[0]?.name || 'Unknown',
            ra: obs.targets?.[0]?.ra || 0,
            dec: obs.targets?.[0]?.dec || 0,
            status: obs.status,
            colour,
            updated_at: obs.updated_at || obs.created_at,
            obsUrl: `/observations/${obs._id || obs.id}`,
            userName,
            filters,
            expInfo,
            capturedAt,
          };
        });
        observationDataRef.current = observationData;

        setPlanetariumMarkers(pointers);
        if (planetariumReadyRef.current) {
          sendPointersToIframe(pointers);
          const iframe = planetariumIframeRef.current;
          if (iframe?.contentWindow) {
            iframe.contentWindow.postMessage({ type: 'set-observations', observations: observationData }, '*');
          }
        }
        // Broadcast to standalone tabs
        if (broadcastChannelRef.current) {
          broadcastChannelRef.current.postMessage({ type: 'set-pointers', pointers });
          broadcastChannelRef.current.postMessage({ type: 'set-observations', observations: observationData });
        }
      } catch (err) {
        console.error('Failed to fetch planetarium markers:', err);
      }
    };

    fetchMarkers();
    // Refresh markers every 5 minutes
    const interval = setInterval(fetchMarkers, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [observatory?._id, observatory?.branding?.planetarium_provider, observatory?.branding?.planetarium_marker_hours, sendPointersToIframe]);

  // BroadcastChannel: create/destroy channel for standalone VirtualSky tabs
  useEffect(() => {
    if (!observatory?._id) return;
    const provider = observatory.branding?.planetarium_provider || 'virtualsky';
    if (provider !== 'virtualsky') return;

    const channel = new BroadcastChannel(`planetarium-${observatory._id}`);
    broadcastChannelRef.current = channel;

    channel.onmessage = (event) => {
      if (event.data?.type === 'request-init') {
        // Respond with init config
        channel.postMessage({
          type: 'init',
          observatory_name: observatory.name,
          overlay_type: observatory.branding?.planetarium_overlay || 'reticle',
          pixel_size: telemetryData?.equipment_status?.camera?.pixel_size,
          sensor_width: telemetryData?.equipment_status?.camera?.sensor_width,
          sensor_height: telemetryData?.equipment_status?.camera?.sensor_height,
          focal_length_mm: telemetryData?.equipment_status?.mount?.focal_length_mm || observatory?.software?.nina?.options?.equipment?.telescope?.focal_length_mm || observatory?.configuration?.telescope_config?.focal_length_mm,
          planetarium_fov: observatory.branding?.planetarium_fov || 60,
          logo_url: constructMinioUrl(observatory.branding?.logo_url) || null,
          logo_link: observatory.branding?.logo_link || null,
          location_city: observatory.location?.city || null,
          location_country: observatory.location?.country || null,
        });
        // Send current markers if we have them
        if (planetariumMarkers.length > 0) {
          channel.postMessage({ type: 'set-pointers', pointers: planetariumMarkers });
        }
        // Send observation data for standalone panel
        if (observationDataRef.current.length > 0) {
          channel.postMessage({ type: 'set-observations', observations: observationDataRef.current });
        }
        // Send current mount state so reticle/fov shows immediately
        const mount = telemetryData?.equipment_status?.mount;
        if (mount?.connected && mount.ra != null && mount.dec != null) {
          channel.postMessage({
            type: 'mount-update',
            ra: mount.ra,
            dec: mount.dec,
            altitude: mount.altitude,
            azimuth: mount.azimuth,
            state: mount.state || (mount.tracking ? 'TRACKING' : mount.parked ? 'PARKED' : mount.slewing ? 'SLEWING' : 'IDLE'),
            connected: mount.connected,
            focal_length_mm: mount.focal_length_mm || observatory?.software?.nina?.options?.equipment?.telescope?.focal_length_mm || observatory?.configuration?.telescope_config?.focal_length_mm,
          });
        }
      }
    };

    return () => {
      channel.close();
      broadcastChannelRef.current = null;
    };
  }, [observatory?._id, observatory?.branding?.planetarium_provider, observatory?.branding?.planetarium_overlay, observatory?.name, observatory?.configuration?.telescope_config?.focal_length_mm, telemetryData?.equipment_status?.camera?.pixel_size, telemetryData?.equipment_status?.camera?.sensor_width, telemetryData?.equipment_status?.camera?.sensor_height, telemetryData?.equipment_status?.mount?.focal_length_mm, planetariumMarkers]);

  // BroadcastChannel + iframe: send mount updates when position changes
  useEffect(() => {
    if (!observatory?._id) return;
    const provider = observatory.branding?.planetarium_provider || 'virtualsky';
    if (provider !== 'virtualsky') return;

    const mount = telemetryData?.equipment_status?.mount;
    if (!mount?.connected || mount.ra == null || mount.dec == null) return;

    // Broadcast to standalone tabs
    if (broadcastChannelRef.current) {
      broadcastChannelRef.current.postMessage({
        type: 'mount-update',
        ra: mount.ra,
        dec: mount.dec,
        altitude: mount.altitude,
        azimuth: mount.azimuth,
        state: mount.state || (mount.tracking ? 'TRACKING' : mount.parked ? 'PARKED' : mount.slewing ? 'SLEWING' : 'IDLE'),
        connected: mount.connected,
        focal_length_mm: mount.focal_length_mm || observatory?.software?.nina?.options?.equipment?.telescope?.focal_length_mm || observatory?.configuration?.telescope_config?.focal_length_mm,
      });
    }

    // Also send center-on-target to embedded iframe if tracking
    if (trackMount) {
      const iframe = planetariumIframeRef.current;
      if (iframe?.contentWindow) {
        iframe.contentWindow.postMessage({
          type: 'center-on-target',
          ra: mount.ra * 15,
          dec: mount.dec,
        }, '*');
      }
    }
  }, [observatory?._id, observatory?.branding?.planetarium_provider, telemetryData?.equipment_status?.mount?.ra, telemetryData?.equipment_status?.mount?.dec, telemetryData?.equipment_status?.mount?.state, telemetryData?.equipment_status?.mount?.connected, trackMount]);

  // Reload observatory data when plugin reconnects to get updated plugin_options
  useEffect(() => {
    if (needsReload) {
      loadObservatory(false);
      setNeedsReload(false);
    }
  }, [needsReload]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!observatory) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Observatory not found</Alert>
      </Box>
    );
  }

  // When the observatory is offline the live telemetry is stale — and a late
  // WebSocket frame can re-populate equipment_status after loadObservatory
  // cleared it — so treat every device as disconnected unless the observatory
  // is online. The last-connected snapshot still drives each card's
  // "Last Reported Status" control, so the values remain available on demand.
  const observatoryOnline = observatory?.status === 'online';
  const rawEquipment = observatoryOnline ? (telemetryData?.equipment_status || {}) : {};
  const system = telemetryData?.system_status || {};
  // Per-device snapshot of the last values reported while a device was
  // connected (each carries an `at` timestamp). Used to power the
  // "Last Reported Status" toggle on disconnected device cards.
  // Prefer the live telemetry copy, but fall back to the observatory payload.
  // `observatory` is set unconditionally from the REST response, whereas
  // telemetryData.last_connected is only populated on the online-status fetch
  // branch — so the fallback covers the offline branch and WS-driven renders
  // where telemetryData never received the snapshot.
  const lastConnected = telemetryData?.last_connected || observatory?.last_connected || {};
  const formatLastSeen = (at) => {
    if (!at) return null;
    try {
      return new Date(at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return null; }
  };
  // Build the per-card equipment view. For a disconnected device whose
  // "Last Reported Status" toggle is on, swap in the last-connected snapshot
  // — it carries connected:true, so the normal connected card layout renders
  // its values unchanged — and mark the device stale so its card is tinted.
  // `rawEquipment` keeps the true live state for the toggle controls.
  const LAST_REPORTED_DEVICES = ['mount', 'camera', 'filterWheel', 'focuser', 'rotator', 'dome', 'weather', 'safetyMonitor', 'switches', 'flatDevice'];
  const staleDevices = {};
  const equipment = { ...rawEquipment };
  for (const k of LAST_REPORTED_DEVICES) {
    if (!rawEquipment[k]?.connected && lastReportedShown[k] && lastConnected[k]) {
      equipment[k] = lastConnected[k];
      staleDevices[k] = true;
    }
  }
  // Renders the toggle button (and, while open, the snapshot timestamp) for a
  // disconnected device that has a last-reported snapshot. Returns null when
  // the device is connected or no snapshot exists.
  const renderLastReportedControl = (key) => {
    if (rawEquipment[key]?.connected) return null;
    if (!lastConnected[key]) return null;
    const showing = !!lastReportedShown[key];
    return (
      <Box sx={{ mt: 0.5 }}>
        <Button
          size="small"
          variant={showing ? 'outlined' : 'text'}
          color="warning"
          onClick={() => setLastReportedShown((s) => ({ ...s, [key]: !s[key] }))}
          sx={{ fontSize: '0.6rem', py: 0, minWidth: 0, textTransform: 'none', lineHeight: 1.3 }}
        >
          {showing ? 'Hide last reported' : 'Last Reported Status'}
        </Button>
        {showing && lastConnected[key]?.at && (
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.55rem', display: 'block', fontStyle: 'italic' }}>
            as of {formatLastSeen(lastConnected[key].at)}
          </Typography>
        )}
      </Box>
    );
  };

  // NINA options from software.nina.options (sent on authentication, stored on observatory)
  const ninaOptions = observatory?.software?.nina?.options;

  // Determine if user can see NINA Options (admin mode, server_admin, or observatory owner)
  const isServerAdmin = user?.role === 'server_admin';
  const isObservatoryOwner = observatory?.owner_id === user?.id ||
                             observatory?.owner_id?._id === user?.id ||
                             (observatory?.members || []).some(m =>
                               (m.user_id === user?.id || m.user_id?._id === user?.id) &&
                               m.role === 'owner'
                             );
  const canViewNinaOptions = mode === 'admin' || isServerAdmin || isObservatoryOwner;

  // Merge astronomical data with system status for display
  const displaySystem = astronomicalData ? { ...system, ...astronomicalData } : system;

  // Set navigation paths based on source and mode
  const fromMyObservatories = mode === 'admin' ? (
    (location && new URLSearchParams(location.search).get('from') === 'my-observatories') ||
    location?.state?.from === 'my-observatories'
  ) : false;

  // For user mode, check if coming from dashboard
  const fromDashboard = mode === 'user' && (
    (location && new URLSearchParams(location.search).get('from') === 'dashboard') ||
    location?.state?.from === 'dashboard' ||
    document.referrer.includes('/dashboard')
  );

  // Determine back path and breadcrumb text
  const backPath = fromMyObservatories ? '/administrative/observatories' :
    (fromDashboard ? '/dashboard' : backRoute);
  const breadcrumbText = fromMyObservatories ? 'My Observatories' :
    (fromDashboard ? 'Dashboard' :
     (backRoute === '/observatories' ? 'Observatories' : 'Admin Observatories'));

  // Standalone (floating-window) mode: render only the requested action
  // dialog, not the full Observatory Details page chrome. The Admin Control
  // Center launches these with standalone=1 (auto-appended), so BOTH supported
  // actions must be handled here — customize (?action=customize) and MOTD
  // (?action=motd). Previously only the MOTD dialog was rendered, so the
  // Customize floating window came up blank. Each dialog closes the floating
  // window on dismiss.
  if (isStandalone) {
    return (
      <Box>
        <ObservatoryPersonalizationDialog
          open={personalizationDialogOpen}
          onClose={() => { setPersonalizationDialogOpen(false); closeFloatingWindow(); }}
          observatory={observatory}
          onSave={() => { loadObservatory(false); loadAccessLevel(); }}
        />
        <CreateMotdDialog
          open={createMotdOpen}
          onClose={() => { setCreateMotdOpen(false); closeFloatingWindow(); }}
          observatoryId={id}
          observatoryName={observatory?.name}
          onCreated={() => { /* parent window will refresh on its own */ }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 3 }}>
        <Link to={backPath} style={{ textDecoration: 'none', color: 'inherit' }}>
          {breadcrumbText}
        </Link>
        <Typography color="text.primary">{observatory.name}</Typography>
      </Breadcrumbs>

      {/* Preview Role Selector - allows owner/admin to preview the page as another role (admin route only) */}
      {enableEditMode && canPersonalize && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, mt: 1 }}>
          <Typography variant="caption" color="text.secondary">Preview as:</Typography>
          {['owner', 'admin', 'user'].map(role => (
            <Chip
              key={role}
              label={role.charAt(0).toUpperCase() + role.slice(1)}
              size="small"
              variant={previewRole === role ? 'filled' : 'outlined'}
              color={previewRole === role ? 'primary' : 'default'}
              onClick={() => setPreviewRole(previewRole === role ? null : role)}
            />
          ))}
          {previewRole && (
            <Chip
              label="Exit Preview"
              size="small"
              color="warning"
              onDelete={() => setPreviewRole(null)}
            />
          )}
        </Box>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          {/* Observatory Logo */}
          {observatory.branding?.logo_url && (() => {
            const sizeMap = { small: 56, medium: 80, large: 120, xlarge: 160, huge: 200 };
            const dim = sizeMap[observatory.branding?.logo_size] || 56;
            return (
            <Box sx={{
              maxWidth: dim,
              flexShrink: 0,
              mt: '0.18em',
            }}>
              {observatory.branding.logo_link ? (
                <a href={observatory.branding.logo_link} target="_blank" rel="noopener noreferrer">
                  <img
                    src={constructMinioUrl(observatory.branding.logo_url)}
                    alt={`${observatory.name} logo`}
                    style={{ maxWidth: dim, maxHeight: dim, objectFit: 'contain', display: 'block', borderRadius: 4, cursor: 'pointer' }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </a>
              ) : (
                <img
                  src={constructMinioUrl(observatory.branding.logo_url)}
                  alt={`${observatory.name} logo`}
                  style={{ maxWidth: dim, maxHeight: dim, objectFit: 'contain', display: 'block', borderRadius: 4 }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
            </Box>
            );
          })()}
          <Box>
            <Typography variant="h4" sx={{ lineHeight: 1 }}>
              {observatory.name}
              {observatory.status === 'inactive' && (
                <Typography component="span" variant="h5" color="error" sx={{ ml: 2, fontWeight: 'bold' }}>
                  INACTIVE
                </Typography>
              )}
              {observatory.code && (
                <Chip
                  label={observatory.code}
                  size="small"
                  sx={{ ml: 2, fontFamily: 'monospace' }}
                  variant="outlined"
                />
              )}
            </Typography>
            {/* Status Badges - unified with Dashboard */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
              {isSectionVisible('location') && (
                <Typography variant="body1" color="text.secondary" sx={{ mr: 1 }}>
                  {[observatory.location?.city, observatory.location?.country].filter(Boolean).join(', ') || 'City, Country missing from Configuration'}
                </Typography>
              )}
              <Chip
                icon={observatory.status === 'online' ? <OnlineIcon /> : <OfflineIcon />}
                label={observatory.status === 'online' ? 'Online' : 'Offline'}
                color={observatory.status === 'online' ? 'success' : 'error'}
                size="small"
              />
              {/* Session State chip (calculated from sun position) - only show when online */}
              {observatory.status === 'online' && observatory.session_state && (() => {
                const sessionInfo = getCalculatedSessionInfo(observatory.session_state);
                if (!sessionInfo) return null;
                const SessionIcon = sessionInfo.icon;
                return (
                  <Tooltip title="Session state calculated from sun position">
                    <Chip
                      icon={<SessionIcon />}
                      label={sessionInfo.label}
                      color={sessionInfo.color}
                      size="small"
                      sx={sessionInfo.sx}
                    />
                  </Tooltip>
                );
              })()}
              {/* Operational State chip (signaled by plugin) - only show when online */}
              {observatory.status === 'online' && observatory.operational_state && (() => {
                const stateInfo = getOperationalStateInfo(observatory.operational_state);
                const StateIcon = stateInfo.icon;
                return (
                  <Tooltip title={`Operational State: ${stateInfo.label}`}>
                    <Chip
                      icon={<StateIcon />}
                      label={stateInfo.label}
                      color={stateInfo.color}
                      size="small"
                    />
                  </Tooltip>
                );
              })()}
              {/* Only show operational chips when online */}
              {observatory.status === 'online' && (
                <>
                  {/* A reservation outranks dispatch_enabled here. While one is
                      active the scheduler refuses every work request, so a green
                      "Dispatching" chip would assert the opposite of what the
                      dispatcher is doing. */}
                  <Chip
                    icon={activeReservation
                      ? <DispatchDisabledIcon />
                      : (observatory.dispatch_enabled !== false ? <DispatchEnabledIcon /> : <DispatchDisabledIcon />)}
                    label={activeReservation
                      ? 'Reserved'
                      : (observatory.dispatch_enabled !== false ? 'Dispatching' : 'Paused')}
                    color={activeReservation
                      ? 'warning'
                      : (observatory.dispatch_enabled !== false ? 'success' : 'default')}
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    icon={equipment.safetyMonitor?.connected ?
                      (equipment.safetyMonitor?.is_safe ? <SafetyIcon /> : <UnsafeIcon />) :
                      <SafetyUnknownIcon />}
                    label={equipment.safetyMonitor?.connected ?
                      (equipment.safetyMonitor?.is_safe ? 'Safe' : 'Unsafe') :
                      'No Safety Monitor'}
                    color={equipment.safetyMonitor?.connected ?
                      (equipment.safetyMonitor?.is_safe ? 'success' : 'error') :
                      'default'}
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    icon={observatory.plugin_options?.operations_enabled !== false && observatory.system_status?.operations_enabled !== false ? <AutomationEnabledIcon /> : <DispatchDisabledIcon />}
                    label={observatory.plugin_options?.operations_enabled !== false && observatory.system_status?.operations_enabled !== false ? 'Automation' : 'Ops Disabled'}
                    color={observatory.plugin_options?.operations_enabled !== false && observatory.system_status?.operations_enabled !== false ? 'success' : 'warning'}
                    size="small"
                    variant="outlined"
                  />
                </>
              )}
            </Box>
            {/* Warning banners - hideable via alerts section visibility */}
            {isSectionVisible('alerts') && (
              <>
                {/* Reserved: the scheduler is refusing work for this observatory right
                    now. Placed first because it is the only banner here that explains
                    why an otherwise healthy, online, dispatch-enabled observatory is
                    sitting idle -- without it the page reads as a fault. */}
                {activeReservation && (
                  <Alert severity="warning" sx={{ mt: 1 }} icon={<ObservatoryIcon />}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      Not Available for Scheduling — Reserved
                    </Typography>
                    <Typography variant="body2">
                      {activeReservation.title}
                      {activeReservation.purpose ? ` — ${activeReservation.purpose}` : ''}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {reservationHeldUntil(activeReservation)
                        ? `Held until ${reservationHeldUntil(activeReservation)}.`
                        : ''}
                      {activeReservation.contact_name ? ` Reserved by ${activeReservation.contact_name}.` : ''}
                      {' '}No observations will be dispatched until the reservation ends.
                    </Typography>
                  </Alert>
                )}
                {/* Null Island warning - coordinates at 0,0 indicate unconfigured location */}
                {observatory.location?.latitude === 0 && observatory.location?.longitude === 0 && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    Observatory located on "Null Island", please correct NINA Latitude and Longitude settings
                  </Alert>
                )}
                {/* Warning banner when operations are disabled for this observatory */}
                {(observatory.plugin_options?.operations_enabled === false || observatory.system_status?.operations_enabled === false) && (
                  <Alert severity="error" sx={{ mt: 1 }} icon={<ObservatoryIcon />}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      Operations Disabled
                    </Typography>
                    <Typography variant="body2">
                      Operations on this observatory are currently marked disabled. To re-enable you must change the setting in the Asterism plugin options.
                    </Typography>
                  </Alert>
                )}
                {/* Warning banner when dark time constraints are bypassed */}
                {observatory.plugin_options?.respect_dark_time_constraints === false && (
                  <Alert severity="warning" sx={{ mt: 1 }} icon={<ObservatoryIcon />}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      Dark Time Constraints Bypassed
                    </Typography>
                    <Typography variant="body2">
                      This observatory can schedule observations during daytime for testing. To disable daytime operations you must change the setting in the Asterism plugin options.
                    </Typography>
                  </Alert>
                )}
                {/* Warning banner when telescope focal length or focal ratio are not configured */}
                {ninaOptions?.equipment?.telescope && (
                  !(ninaOptions?.equipment?.telescope.focal_length_mm > 0) ||
                  !(ninaOptions?.equipment?.telescope.focal_ratio > 0)
                ) && (
                  <Alert severity="warning" sx={{ mt: 1 }} icon={<MountIcon />}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      Telescope Configuration Missing
                    </Typography>
                    <Typography variant="body2">
                      {!(ninaOptions?.equipment?.telescope.focal_length_mm > 0) && !(ninaOptions?.equipment?.telescope.focal_ratio > 0)
                        ? 'Focal Length and Focal Ratio are'
                        : !(ninaOptions?.equipment?.telescope.focal_length_mm > 0)
                          ? 'Focal Length is'
                          : 'Focal Ratio is'
                      } not configured - may cause Plate Solve or Autofocus issues. To set the proper value refer to the Options - Equipment page in the NINA Application.
                    </Typography>
                  </Alert>
                )}
                {/* Warning banner when dome radius is not properly configured (only for rotating domes, not roll-off roofs) */}
                {equipment.dome?.connected && equipment.dome?.can_set_azimuth && ninaOptions?.dome && !(ninaOptions?.dome?.dome_radius_mm > 0) && (
                  <Alert severity="warning" sx={{ mt: 1 }} icon={<DomeIcon />}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      Dome Radius Not Configured
                    </Typography>
                    <Typography variant="body2">
                      The Dome radius is not properly configured. This may result in NINA errors and may be the cause of Observation warning messages. To set the proper value refer to the Options - Dome page in the NINA Application.
                    </Typography>
                  </Alert>
                )}
              </>
            )}
            {isSectionVisible('current_observation') && currentObservation && (
              <Box sx={{ mt: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={currentObservation.status === 'in_progress' ? 'OBSERVING' : 'ASSIGNED'}
                    color={currentObservation.status === 'in_progress' ? 'success' : 'info'}
                    size="small"
                  />
                  <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'medium' }}>
                    {currentObservation.target?.name || currentObservation.name || 'Unknown Target'}
                  </Typography>
                  {currentObservation.target?.ra && currentObservation.target?.dec && (
                    <Typography variant="body2" color="text.secondary">
                      (RA: {currentObservation.target.ra.toFixed(2)}°, Dec: {currentObservation.target.dec.toFixed(2)}°)
                    </Typography>
                  )}
                </Box>
                {/* Display scheduled end time from scheduler timeline */}
                {(() => {
                  // Use utc_end_time - this is the authoritative deadline from the scheduler
                  // If an observation starts late, it still must end at this time
                  const displayTime = currentObservation.utc_end_time ? new Date(currentObservation.utc_end_time) : null;

                  if (!displayTime) return null;

                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        Scheduled End: {(() => {
                          const hours = displayTime.getUTCHours().toString().padStart(2, '0');
                          const minutes = displayTime.getUTCMinutes().toString().padStart(2, '0');
                          const seconds = displayTime.getUTCSeconds().toString().padStart(2, '0');
                          return `${hours}:${minutes}:${seconds} UTC`;
                        })()}
                      </Typography>
                      {['assigned', 'in_progress'].includes(currentObservation.status) && (
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 'medium',
                            color: (() => {
                              const remaining = displayTime - new Date();
                              return remaining <= 0 ? 'error.main' : 'primary.main';
                            })()
                          }}
                        >
                          {(() => {
                            const remaining = displayTime - new Date();
                            if (remaining <= 0) return 'Time Expired';

                            const hours = Math.floor(remaining / (1000 * 60 * 60));
                            const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
                            const seconds = Math.floor((remaining % (1000 * 60)) / 1000);

                            if (hours > 0) {
                              return `${hours}h ${minutes}m remaining`;
                            } else if (minutes > 0) {
                              return `${minutes}m ${seconds}s remaining`;
                            } else {
                              return `${seconds}s remaining`;
                            }
                          })()}
                        </Typography>
                      )}
                    </Box>
                  );
                })()}
                <Box sx={{ mt: 1 }}>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => {
                      const observationId = currentObservation._id;
                      setConfirm({
                        title: 'Cancel observation?',
                        message: 'Are you sure you want to cancel this observation? This will stop the observation immediately.',
                        confirmLabel: 'Cancel observation',
                        confirmColor: 'error',
                        confirmIcon: <OfflineIcon />,
                        icon: <OfflineIcon color="error" />,
                        onConfirm: async () => {
                          try {
                            await apiService.cancelObservation(
                              observationId,
                              'Cancelled from observatory page'
                            );
                            // Reload the observation status
                            const currentObs = await apiService.getObservatoryCurrentObservation(id);
                            setCurrentObservation(currentObs);
                          } catch (error) {
                            console.error('Failed to cancel observation:', error);
                            alert('Failed to cancel observation: ' + (error.response?.data?.error?.message || error.message));
                            throw error;
                          }
                        },
                      });
                    }}
                  >
                    Cancel Observation
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
        {/* Action buttons - two rows, upper right, all same width */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flexShrink: 0, '& .MuiButton-root': { minWidth: 130, justifyContent: 'flex-start' } }}>
          {/* Top row: Users, Customize, Edit */}
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            {enableEditMode && canPersonalize && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<MembersIcon />}
                onClick={() => navigate(`/admin/observatories/${id}/users${fromMyObservatories ? '?from=my-observatories' : ''}`)}
              >
                Users
              </Button>
            )}
            {enableEditMode && canPersonalize && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<PersonalizeIcon />}
                onClick={() => setPersonalizationDialogOpen(true)}
              >
                Customize
              </Button>
            )}
            {enableEditMode && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<EditIcon />}
                onClick={() => navigate(`/admin/observatories?edit=${id}`)}
              >
                Edit
              </Button>
            )}
            {enableEditMode && observatory?.api_key_hash && (
              <Button
                variant="outlined"
                size="small"
                color="warning"
                startIcon={<FingerprintIcon />}
                onClick={handleResetFingerprint}
              >
                Reset Fingerprint
              </Button>
            )}
            {enableEditMode && canPersonalize && (
              <Button
                variant="outlined"
                size="small"
                color="info"
                startIcon={<AnnouncementIcon />}
                onClick={() => setCreateMotdOpen(true)}
              >
                New MOTD
              </Button>
            )}
          </Box>
          {/* Bottom row: Timeline, Multi-Day Schedule, Refresh */}
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<TimelineIcon />}
              onClick={() => navigate(`/timeline/${id}`)}
            >
              Timeline
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<CalendarIcon />}
              onClick={() => navigate(`/multi-day-timeline/${id}`)}
            >
              Multi-Day Schedule
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() => loadObservatory(false)}
            >
              Refresh
            </Button>
          </Box>
          {/* Resolved subscription tier — under the action buttons, click for entitlements */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <ObservatorySubscriptionCard observatoryId={id} sx={{ width: { xs: '100%', sm: 260 } }} />
          </Box>
        </Box>
      </Box>

      {/* Active MOTD banners */}
      {activeMotds.length > 0 && activeMotds.map(motd => (
        <Alert
          key={motd._id}
          severity={motd.severity === 'critical' ? 'error' : motd.severity}
          sx={{ mb: 2, cursor: 'pointer' }}
          onClick={() => { setSelectedMotd(motd); setMotdDialogOpen(true); }}
          action={
            <Button color="inherit" size="small" onClick={(e) => {
              e.stopPropagation();
              setSelectedMotd(motd);
              setMotdDialogOpen(true);
            }}>
              Acknowledge
            </Button>
          }
        >
          <Typography variant="h5">{motd.title}</Typography>
          <Typography variant="body2">{motd.message}</Typography>
        </Alert>
      ))}

      {/* Announcements — show active MOTDs to all users */}
      {(() => {
        const activeAnnouncements = allMotds.filter(m => m.is_active && m.motd_status === 'active' && new Date(m.expires_at) > new Date());
        if (activeAnnouncements.length === 0) return null;
        return (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Announcements ({activeAnnouncements.length})</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Title</TableCell>
                      <TableCell>Severity</TableCell>
                      <TableCell>Expires</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activeAnnouncements.map((motd) => (
                      <TableRow key={motd._id}>
                        <TableCell>{motd.title}</TableCell>
                        <TableCell><Chip label={motd.severity} color={motd.severity === 'critical' ? 'error' : motd.severity} size="small" /></TableCell>
                        <TableCell>{new Date(motd.expires_at).toLocaleDateString()}</TableCell>
                        <TableCell align="right">
                          <Tooltip title="Acknowledge">
                            <IconButton size="small" color="success" onClick={() => handleAcknowledgeMotdFromTable(motd._id)}>
                              <AcknowledgeIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {(enableEditMode && canPersonalize) && (
                            <>
                              <Tooltip title="View Recipients">
                                <IconButton size="small" onClick={() => handleViewMotdRecipients(motd._id)}>
                                  <ViewRecipientsIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Cancel Announcement">
                                <IconButton size="small" color="error" onClick={() => { setCancelMotdTarget(motd); setCancelMotdReason(''); setCancelMotdOpen(true); }}>
                                  <CancelMotdIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        );
      })()}

      {/* Cancel MOTD Dialog */}
      <Dialog open={cancelMotdOpen} onClose={() => setCancelMotdOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cancel Announcement</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="warning">
              Cancel announcement "{cancelMotdTarget?.title}"? Users who have not yet acknowledged it will no longer see it.
            </Alert>
            <TextField
              label="Reason for cancellation"
              value={cancelMotdReason}
              onChange={(e) => setCancelMotdReason(e.target.value)}
              fullWidth
              required
              multiline
              rows={2}
              placeholder="Why is this announcement being cancelled?"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelMotdOpen(false)}>Keep Active</Button>
          <Button onClick={handleCancelMotdConfirm} variant="contained" color="error" disabled={!cancelMotdReason.trim()}>
            Cancel Announcement
          </Button>
        </DialogActions>
      </Dialog>

      {/* MOTD Recipients Dialog */}
      <Dialog open={motdRecipientsOpen} onClose={() => { setMotdRecipientsOpen(false); setMotdRecipientsData(null); }} maxWidth="md" fullWidth>
        <DialogTitle>
          Recipients — {motdRecipientsData?.title}
          {motdRecipientsData && (
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Chip label={`${motdRecipientsData.acknowledged_count} acknowledged`} color="success" size="small" />
              <Chip label={`${motdRecipientsData.pending_count} pending`} color="warning" size="small" />
              <Chip label={`${motdRecipientsData.total_recipients} total`} size="small" variant="outlined" />
            </Box>
          )}
          {motdRecipientsData?.message && (
            <Alert severity={motdRecipientsData.severity === 'critical' ? 'error' : motdRecipientsData.severity || 'info'} sx={{ mt: 2 }}>
              {motdRecipientsData.message}
            </Alert>
          )}
        </DialogTitle>
        <DialogContent>
          {motdRecipientsLoading ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          ) : motdRecipientsData?.recipients ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Acknowledged At</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {motdRecipientsData.recipients.map((r) => (
                    <TableRow key={r._id}>
                      <TableCell>{r.first_name} {r.last_name}</TableCell>
                      <TableCell>{r.email}</TableCell>
                      <TableCell>
                        <Chip label={r.acknowledged ? 'Acknowledged' : 'Pending'} color={r.acknowledged ? 'success' : 'warning'} size="small" />
                      </TableCell>
                      <TableCell>{r.acknowledged_at ? new Date(r.acknowledged_at).toLocaleString() : '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info">No recipient data available</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setMotdRecipientsOpen(false); setMotdRecipientsData(null); }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Dynamic section rendering based on section_order */}
      {(() => {
        // Compute effective section order from saved order, falling back to defaults
        const DEFAULT_SECTION_ORDER = [
          'branding', 'compact_status', 'equipment',
          'system_status', 'observatory_tabs', 'clear_sky_chart', 'live_feeds', 'navigation_buttons',
        ];
        const savedOrder = accessLevelData?.section_order;
        let effectiveOrder;
        if (savedOrder && Array.isArray(savedOrder) && savedOrder.length > 0) {
          const validSaved = savedOrder.filter(key => DEFAULT_SECTION_ORDER.includes(key));
          const missing = DEFAULT_SECTION_ORDER.filter(key => !validSaved.includes(key));
          effectiveOrder = [...validSaved, ...missing];
        } else {
          effectiveOrder = DEFAULT_SECTION_ORDER;
        }

        // Section renderers - each returns JSX or null
        const sectionRenderers = {
          branding: () => {
            if (!isSectionVisible('branding') || !observatory.branding?.description) return null;
            return (
              <Paper sx={{ p: 2, overflow: 'hidden' }} elevation={2}>
                <Box
                  sx={{
                    '& a': { color: 'primary.main' },
                    '& img': { maxWidth: '100% !important', height: 'auto !important' },
                  }}
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(observatory.branding.description) }}
                />
              </Paper>
            );
          },

          compact_status: () => {
            if (!isSectionVisible('compact_status')) return null;
            // Show no-telemetry alert when there's no telemetry data
            if (!telemetryData) {
              return (
                <Alert severity="warning">
                  No telemetry data received. Ensure the observatory is connected and sending status updates.
                </Alert>
              );
            }
            return (
        <>
        <Typography variant="h5" sx={{ mb: 2 }}>Status Summary</Typography>
        <Paper sx={{
          p: 1.5,
          bgcolor: 'background.default',
          border: isDataStale ? '2px solid' : '1px solid',
          borderColor: isDataStale ? 'warning.main' : 'divider',
          maxWidth: 1344
        }} variant="outlined">
          {/* Stale Data Warning */}
          {isDataStale && (
            <Alert severity="warning" sx={{ mb: 1, py: 0.5 }}>
              Data may be outdated - Last update: {lastUpdate ? formatDistanceToNow(lastUpdate, { addSuffix: true }) : 'Unknown'}
            </Alert>
          )}
          {/* Primary Status Row */}
          {(() => { const hideDisc = accessLevelData?.page_behavior?.hide_disconnected_equipment; const hideDisconnected = hideDisc && hideDisc[getRoleTier()]; return (
          <Grid container spacing={1.5}>
            {/* Mount Status */}
            {(!hideDisconnected || equipment.mount?.connected || lastConnected.mount) && <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: 'center', ...(staleDevices.mount && { bgcolor: 'warning.light', borderRadius: 1, py: 0.5 }) }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  MOUNT
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium', fontSize: '0.95rem' }}>
                  {equipment.mount?.connected ?
                    (equipment.mount?.state || (equipment.mount?.tracking ? 'TRACKING' : equipment.mount?.parked ? 'PARKED' : equipment.mount?.slewing ? 'SLEWING' : 'IDLE')) :
                    'OFFLINE'}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  {equipment.mount?.connected ?
                    (equipment.mount?.state_detail || equipment.mount?.tracking_mode || 'N/A') :
                    'N/A'}
                </Typography>
                {(equipment.mount?.connected && equipment.mount?.focal_length_mm) || observatory?.configuration?.telescope_config?.focal_length_mm ? (
                  <Typography variant="caption" color="primary" sx={{ fontSize: '0.65rem', display: 'block', mt: 0.5 }}>
                    {(equipment.mount?.aperture_mm || observatory?.configuration?.telescope_config?.aperture_mm) ?
                      `${equipment.mount?.aperture_mm || observatory.configuration.telescope_config.aperture_mm}mm` : ''}
                    f/{equipment.mount?.focal_ratio ||
                       (observatory?.configuration?.telescope_config?.focal_length_mm && observatory?.configuration?.telescope_config?.aperture_mm ?
                         (observatory.configuration.telescope_config.focal_length_mm / observatory.configuration.telescope_config.aperture_mm).toFixed(1) : '?')}
                    {(equipment.mount?.focal_length_mm || observatory?.configuration?.telescope_config?.focal_length_mm) ?
                      ` (${equipment.mount?.focal_length_mm || observatory.configuration.telescope_config.focal_length_mm}mm)` : ''}
                  </Typography>
                ) : null}
              </Box>
            </Grid>}

            {/* Camera Status */}
            {(!hideDisconnected || equipment.camera?.connected || lastConnected.camera) && <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: 'center', ...(staleDevices.camera && { bgcolor: 'warning.light', borderRadius: 1, py: 0.5 }) }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  CAMERA
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium', fontSize: '0.95rem' }}>
                  {equipment.camera?.connected ? 
                    (equipment.camera?.is_exposing ? 'EXPOSING' :
                     equipment.camera?.is_active ? equipment.camera?.camera_state?.toUpperCase() || 'ACTIVE' :
                     `${equipment.camera?.temperature?.toFixed(1) || '--'}°C`) : 
                    'OFFLINE'}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  {equipment.camera?.connected ?
                    (equipment.camera?.is_exposing ?
                      (equipment.camera?.exposure_index && equipment.camera?.exposure_count ?
                        `${equipment.camera.exposure_index}/${equipment.camera.exposure_count} - ${equipment.camera?.exposure_remaining !== undefined ?
                          `${equipment.camera.exposure_remaining.toFixed(0)}s left` :
                          `${equipment.camera?.exposure_elapsed?.toFixed(0) || '0'}s`}` :
                        equipment.camera?.exposure_remaining !== undefined ?
                        `${equipment.camera.exposure_remaining.toFixed(0)}s remaining` :
                        equipment.camera?.exposure_elapsed !== undefined ?
                        `${equipment.camera.exposure_elapsed.toFixed(0)}s elapsed` :
                        'Capturing Image') :
                      equipment.camera?.is_active ?
                      'Camera Active' :
                      equipment.camera?.cooler_on ?
                      `Cooling ${equipment.camera?.cooler_power?.toFixed(0) || '--'}%` :
                      'Cooler Off') :
                    'N/A'}
                </Typography>
              </Box>
            </Grid>}

            {/* Weather Status */}
            {(!hideDisconnected || equipment.weather?.connected || lastConnected.weather) && <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: 'center', ...(staleDevices.weather && { bgcolor: 'warning.light', borderRadius: 1, py: 0.5 }) }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  WEATHER
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium', fontSize: '0.95rem' }}>
                  {equipment.weather?.connected ?
                    `${equipment.weather?.temperature != null ? Number(equipment.weather.temperature).toFixed(1) : '--'}°C` :
                    'OFFLINE'}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  {equipment.weather?.connected ?
                    `${equipment.weather?.humidity != null ? Number(equipment.weather.humidity).toFixed(0) : '--'}% RH` :
                    'N/A'}
                </Typography>
              </Box>
            </Grid>}

            {/* Dome Status */}
            {(!hideDisconnected || equipment.dome?.connected || lastConnected.dome) && <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: 'center', ...(staleDevices.dome && { bgcolor: 'warning.light', borderRadius: 1, py: 0.5 }) }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  DOME
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium', fontSize: '0.95rem' }}>
                  {equipment.dome?.connected ? 
                    equipment.dome?.shutter_status?.toUpperCase() || 'UNKNOWN' : 
                    'OFFLINE'}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  {equipment.dome?.connected && equipment.dome?.at_park ? 'Parked' :
                   equipment.dome?.connected && equipment.dome?.slewing ? 'Slewing' :
                   equipment.dome?.connected && typeof equipment.dome?.azimuth === 'number' ? `Az: ${equipment.dome.azimuth.toFixed(0)}°` :
                   equipment.dome?.connected ? '' : 'N/A'}
                </Typography>
              </Box>
            </Grid>}

            {/* Sun Status - moved to primary row */}
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>SUN</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium', fontSize: '0.95rem' }}>
                  {astronomyService.formatAltitude(displaySystem.sun_altitude)}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  {displaySystem.sun_altitude !== undefined ?
                    (displaySystem.sun_altitude > 0 ? 'Day' : 
                     displaySystem.sun_altitude > -6 ? 'Civil' :
                     displaySystem.sun_altitude > -12 ? 'Nautical' :
                     displaySystem.sun_altitude > -18 ? 'Astro' : 'Night') :
                    ''}
                </Typography>
                {displaySystem.next_sun_event && (
                  <>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'block' }}>
                      {displaySystem.next_sun_event_type === 'dawn' ? 'Dawn' : 
                       displaySystem.next_sun_event_type === 'dusk' ? 'Dusk' :
                       displaySystem.next_sun_event_type === 'sunrise' ? 'Rise' : 'Set'}: {
                        new Date(displaySystem.next_sun_event).toISOString().substring(11, 16)
                      } UTC
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'block' }}>
                      in {astronomyService.calculateTimeRemaining(displaySystem.next_sun_event)}
                    </Typography>
                  </>
                )}
                {displaySystem.twilight_type && (
                  <Typography variant="caption" color="primary" sx={{ fontSize: '0.6rem', display: 'block', mt: 0.5 }}>
                    {displaySystem.twilight_type === 'custom' ? 
                      `Custom (${displaySystem.twilight_altitude}°)` : 
                      displaySystem.twilight_type.charAt(0).toUpperCase() + displaySystem.twilight_type.slice(1)}
                  </Typography>
                )}
              </Box>
            </Grid>
            
            {/* Moon Status - moved to primary row */}
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>MOON</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium', fontSize: '0.95rem' }}>
                  {astronomyService.formatAltitude(displaySystem.moon_altitude)}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  {displaySystem.moon_altitude != null ? (
                    <>
                      {astronomyService.formatIllumination(displaySystem.moon_illumination)}
                      {displaySystem.moon_phase ? ` ${displaySystem.moon_phase}` : ''}
                    </>
                  ) : '\u00A0'}
                </Typography>
              </Box>
            </Grid>
            
            {/* Safety Status - moved to primary row */}
            {(!hideDisconnected || equipment.safetyMonitor?.connected || lastConnected.safetyMonitor) && <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: 'center', ...(staleDevices.safetyMonitor && { bgcolor: 'warning.light', borderRadius: 1, py: 0.5 }) }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>SAFETY</Typography>
                <Typography variant="body1" sx={{
                  fontWeight: 'medium',
                  fontSize: '0.95rem',
                  color: equipment.safetyMonitor?.connected ?
                    (equipment.safetyMonitor?.is_safe ? 'success.main' : 'error.main') :
                    'text.primary'
                }}>
                  {equipment.safetyMonitor?.connected ?
                    (equipment.safetyMonitor?.is_safe ? 'SAFE' : 'UNSAFE') :
                    'OFFLINE'}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  &nbsp;
                </Typography>
              </Box>
            </Grid>}
          </Grid>
          ); })()}
          
          {/* Quick Stats Bar */}
          <Divider sx={{ my: 1 }} />
          <Grid container spacing={1} sx={{ textAlign: 'center' }}>
            <Grid item xs={2}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', display: 'block' }}>LST</Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.9rem', fontWeight: 'medium' }}>
                {astronomyService.formatLST(displaySystem.local_sidereal_time)}
              </Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', display: 'block' }}>RA/Dec</Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.9rem', fontWeight: 'medium' }}>
                {equipment.mount?.connected ?
                  `${decimalToSexagesimal(equipment.mount?.ra, true).substring(0, 8)} / ${decimalToSexagesimal(equipment.mount?.dec, false).substring(0, 9)}` :
                  'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', display: 'block' }}>Alt/Az</Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.9rem', fontWeight: 'medium' }}>
                {equipment.mount?.connected ?
                  `${equipment.mount?.altitude?.toFixed(1) || '--'}° / ${equipment.mount?.azimuth?.toFixed(1) || '--'}°` :
                  'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', display: 'block' }}>Focus</Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.9rem', fontWeight: 'medium' }}>
                {equipment.focuser?.connected ?
                  equipment.focuser?.position || 'N/A' :
                  'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', display: 'block' }}>Filter</Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.9rem', fontWeight: 'medium' }}>
                {equipment.filterWheel?.connected ?
                  equipment.filterWheel?.current_filter || 'Unknown' :
                  'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', display: 'block' }}>Pier Side</Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.9rem', fontWeight: 'medium' }}>
                {equipment.mount?.connected && equipment.mount?.pier_side !== 'None' ?
                  equipment.mount?.pier_side :
                  'N/A'}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
        </>
            );
          },

          equipment: () => {
            if (!isAnyEquipmentVisible()) return null;
            return (
              <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Equipment Status</Typography>
        {/* NINA Options dropdown - visible to admin mode, server_admin, or observatory owner */}
        {canViewNinaOptions && isSectionVisible('nina_options') && (
          <>
            <Button
              variant="outlined"
              size="small"
              startIcon={<SettingsIcon />}
              endIcon={<ArrowDropDownIcon />}
              onClick={(e) => setNinaOptionsMenuAnchor(e.currentTarget)}
              disabled={!ninaOptions}
            >
              NINA Options
            </Button>
            <Menu
              anchorEl={ninaOptionsMenuAnchor}
              open={Boolean(ninaOptionsMenuAnchor)}
              onClose={() => setNinaOptionsMenuAnchor(null)}
            >
              <MenuItem onClick={() => { setNinaOptionsDialogType('general'); setNinaOptionsDialogOpen(true); setNinaOptionsMenuAnchor(null); }}>
                General
              </MenuItem>
              <MenuItem onClick={() => { setNinaOptionsDialogType('equipment'); setNinaOptionsDialogOpen(true); setNinaOptionsMenuAnchor(null); }}>
                Equipment
              </MenuItem>
              <MenuItem onClick={() => { setNinaOptionsDialogType('autofocus'); setNinaOptionsDialogOpen(true); setNinaOptionsMenuAnchor(null); }}>
                AutoFocus
              </MenuItem>
              <MenuItem onClick={() => { setNinaOptionsDialogType('dome'); setNinaOptionsDialogOpen(true); setNinaOptionsMenuAnchor(null); }}>
                Dome
              </MenuItem>
              <MenuItem onClick={() => { setNinaOptionsDialogType('imaging'); setNinaOptionsDialogOpen(true); setNinaOptionsMenuAnchor(null); }}>
                Imaging
              </MenuItem>
              <MenuItem onClick={() => { setNinaOptionsDialogType('platesolving'); setNinaOptionsDialogOpen(true); setNinaOptionsMenuAnchor(null); }}>
                Plate Solving
              </MenuItem>
            </Menu>
          </>
        )}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
        {/* Mount */}
        {isEquipmentVisible('mount') && <Box sx={{ width: 512, maxWidth: '100%' }}>
          <EquipmentStatusCard
            title="Mount"
            icon={<MountIcon color="primary" />}
            connected={equipment.mount?.connected}
            data={equipment.mount}
            stale={staleDevices.mount}
            lastReportedControl={renderLastReportedControl('mount')}
            equipmentName={ninaOptions?.equipment?.telescope?.name || equipment.mount?.configured_name || equipment.mount?.name}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Status:</Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {equipment.mount?.state ? (
                    <>
                      <Chip 
                        label={equipment.mount.state}
                        size="small" 
                        color={
                          equipment.mount.state === 'TRACKING' ? 'primary' :
                          equipment.mount.state === 'GUIDING' ? 'success' :
                          equipment.mount.state === 'PARKED' ? 'default' :
                          equipment.mount.state === 'AT_HOME' ? 'info' :
                          equipment.mount.state === 'PARKING' || equipment.mount.state === 'HOMING' ? 'warning' :
                          equipment.mount.state === 'SLEWING' ? 'warning' :
                          'default'
                        }
                      />
                      {equipment.mount.state_detail && (
                        <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                          ({equipment.mount.state_detail})
                        </Typography>
                      )}
                    </>
                  ) : (
                    // Fallback to old logic
                    <>
                      {equipment.mount?.parked && <Chip label="Parked" size="small" />}
                      {equipment.mount?.tracking && (
                        <Chip 
                          label={`Tracking (${formatTrackingMode(equipment.mount?.tracking_mode || equipment.mount?.tracking_rate, equipment.mount?.custom_ra_rate, equipment.mount?.custom_dec_rate)})`} 
                          size="small" 
                          color="primary" 
                        />
                      )}
                      {equipment.mount?.slewing && <Chip label="Slewing" size="small" color="warning" />}
                      {!equipment.mount?.parked && !equipment.mount?.tracking && !equipment.mount?.slewing && (
                        <Chip label="Idle" size="small" color="default" />
                      )}
                    </>
                  )}
                </Box>
              </Box>
              {equipment.mount?.at_home && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Home:</Typography>
                  <Chip label="At Home" size="small" color="info" />
                </Box>
              )}
              {equipment.mount?.is_pulse_guiding && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Guiding:</Typography>
                  <Chip label="Active" size="small" color="success" />
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">RA:</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {decimalToSexagesimal(equipment.mount?.ra, true)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Dec:</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {decimalToSexagesimal(equipment.mount?.dec, false)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Alt/Az:</Typography>
                <Typography variant="body2">
                  {equipment.mount?.altitude?.toFixed(1)}° / {equipment.mount?.azimuth?.toFixed(1)}°
                </Typography>
              </Box>
              {equipment.mount?.pier_side &&
               equipment.mount.pier_side !== 'pierUnknown' &&
               equipment.mount.pier_side !== 'None' && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Pier Side:</Typography>
                  <Typography variant="body2">{equipment.mount.pier_side}</Typography>
                </Box>
              )}
              {/* Telescope Specifications from NINA Equipment Options - always show */}
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>Telescope Details</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Focal Length:</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {ninaOptions?.equipment?.telescope?.focal_length_mm > 0
                    ? `${ninaOptions?.equipment?.telescope.focal_length_mm}mm`
                    : '-'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Focal Ratio:</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {ninaOptions?.equipment?.telescope?.focal_ratio > 0
                    ? `f/${ninaOptions?.equipment?.telescope.focal_ratio}`
                    : '-'}
                </Typography>
              </Box>
              {/* Mount Settings from NINA Equipment Options */}
              {ninaOptions?.equipment?.mount && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Settle Time:</Typography>
                  <Typography variant="body2">{ninaOptions?.equipment?.mount.settle_time_seconds}s</Typography>
                </Box>
              )}
              {ninaOptions?.equipment?.mount?.no_sync && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Sync:</Typography>
                  <Chip label="Disabled" size="small" color="warning" />
                </Box>
              )}
              {/* Driver Info */}
              {equipment.mount?.driver_version && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>Driver</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Version:</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{equipment.mount.driver_version}</Typography>
                  </Box>
                </>
              )}
            </Box>
          </EquipmentStatusCard>
        </Box>}

        {/* Camera */}
        {isEquipmentVisible('camera') && <Box sx={{ width: 512, maxWidth: '100%' }}>
          <EquipmentStatusCard
            title="Camera"
            icon={<CameraIcon color="primary" />}
            connected={equipment.camera?.connected}
            data={equipment.camera}
            stale={staleDevices.camera}
            lastReportedControl={renderLastReportedControl('camera')}
            equipmentName={equipment.camera?.name}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {equipment.camera?.temperature !== null && equipment.camera?.temperature !== undefined && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Temperature:</Typography>
                    <Typography variant="body2">
                      {equipment.camera.temperature.toFixed(2)}°C
                    </Typography>
                  </Box>
                  {equipment.camera?.target_temperature !== null && equipment.camera?.target_temperature !== undefined && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">Target Temp:</Typography>
                      <Typography variant="body2">
                        {equipment.camera.target_temperature.toFixed(2)}°C
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Cooler:</Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Chip 
                        label={equipment.camera?.cooler_on ? 'ON' : 'OFF'} 
                        size="small" 
                        color={equipment.camera?.cooler_on ? 'success' : 'default'}
                      />
                      {equipment.camera?.cooler_power !== null && equipment.camera?.cooler_power !== undefined && (
                        <Typography variant="body2">{Math.round(equipment.camera?.cooler_power)}%</Typography>
                      )}
                    </Box>
                  </Box>
                </>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Binning:</Typography>
                <Typography variant="body2">{equipment.camera?.bin_x}x{equipment.camera?.bin_y}</Typography>
              </Box>
              {(equipment.camera?.gain !== undefined || equipment.camera?.offset !== undefined) && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Gain/Offset:</Typography>
                  <Typography variant="body2">
                    {equipment.camera?.gain !== undefined ? equipment.camera.gain : 'N/A'}/
                    {equipment.camera?.offset !== undefined ? equipment.camera.offset : 'N/A'}
                  </Typography>
                </Box>
              )}
              {equipment.camera?.camera_state && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">State:</Typography>
                  <Chip 
                    label={equipment.camera?.camera_state} 
                    size="small" 
                    color={equipment.camera?.is_exposing ? 'primary' : 
                           equipment.camera?.is_active ? 'warning' : 'default'}
                    sx={{ fontWeight: equipment.camera?.is_exposing ? 'bold' : 'normal' }}
                  />
                </Box>
              )}
              
              {/* Sensor Details */}
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>Sensor Details</Typography>
              
              {equipment.camera?.sensor_name && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Sensor:</Typography>
                  <Typography variant="body2" sx={{ fontStyle: 'italic' }}>{equipment.camera.sensor_name}</Typography>
                </Box>
              )}
              
              {equipment.camera?.sensor_type && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Type:</Typography>
                  <Typography variant="body2">{equipment.camera.sensor_type}</Typography>
                </Box>
              )}
              
              {equipment.camera?.pixel_size !== null && equipment.camera?.pixel_size !== undefined && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Pixel Size:</Typography>
                  <Typography variant="body2">{equipment.camera.pixel_size.toFixed(2)} μm</Typography>
                </Box>
              )}
              
              {equipment.camera?.sensor_width && equipment.camera?.sensor_height && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Resolution:</Typography>
                  <Typography variant="body2">{equipment.camera.sensor_width} × {equipment.camera.sensor_height}</Typography>
                </Box>
              )}
              {equipment.camera?.is_exposing && equipment.camera?.exposure_elapsed !== undefined && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Typography variant="body2">
                      {equipment.camera?.exposure_index && equipment.camera?.exposure_count ?
                        `Exposure ${equipment.camera.exposure_index}/${equipment.camera.exposure_count}:` :
                        'Exposure:'}
                    </Typography>
                    <Typography variant="body2">
                      {equipment.camera?.exposure_total ? 
                        `${equipment.camera.exposure_elapsed.toFixed(0)}s / ${equipment.camera.exposure_total}s` : 
                        `${equipment.camera.exposure_elapsed.toFixed(1)}s elapsed`}
                    </Typography>
                  </Box>
                  {equipment.camera?.exposure_remaining !== undefined && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">Time Remaining:</Typography>
                      <Typography variant="body2" color="primary">
                        {equipment.camera.exposure_remaining.toFixed(0)}s
                      </Typography>
                    </Box>
                  )}
                  {equipment.camera?.exposure_progress !== undefined && (
                    <Box sx={{ mt: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption">Progress</Typography>
                        <Typography variant="caption">
                          {equipment.camera.exposure_progress.toFixed(0)}%
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={equipment.camera.exposure_progress} 
                        sx={{ height: 6, borderRadius: 1 }}
                      />
                    </Box>
                  )}
                </>
              )}
              {/* Driver Info */}
              {equipment.camera?.driver_version && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>Driver</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Version:</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{equipment.camera.driver_version}</Typography>
                  </Box>
                </>
              )}
            </Box>
          </EquipmentStatusCard>
        </Box>}

        {/* Filter Wheel */}
        {isEquipmentVisible('filter_wheel') && <Box sx={{ width: 512, maxWidth: '100%' }}>
          <EquipmentStatusCard
            title="Filter Wheel"
            icon={<FilterIcon color="primary" />}
            connected={equipment.filterWheel?.connected}
            data={equipment.filterWheel}
            stale={staleDevices.filterWheel}
            lastReportedControl={renderLastReportedControl('filterWheel')}
            equipmentName={equipment.filterWheel?.name}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Current Filter:</Typography>
                <Chip label={equipment.filterWheel?.current_filter || 'Unknown'} size="small" color="secondary" />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Position:</Typography>
                <Typography variant="body2">{equipment.filterWheel?.position}</Typography>
              </Box>
              {equipment.filterWheel?.filter_names?.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>Available Filters:</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {equipment.filterWheel.filter_names.map((filter, idx) => (
                      <Chip key={idx} label={filter} size="small" variant="outlined" />
                    ))}
                  </Box>
                </Box>
              )}
              {/* Driver Info */}
              {equipment.filterWheel?.driver_version && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>Driver</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Version:</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{equipment.filterWheel.driver_version}</Typography>
                  </Box>
                </>
              )}
            </Box>
          </EquipmentStatusCard>
        </Box>}

        {/* Focuser */}
        {isEquipmentVisible('focuser') && <Box sx={{ width: 512, maxWidth: '100%' }}>
          <EquipmentStatusCard
            title="Focuser"
            icon={<FocuserIcon color="primary" />}
            connected={equipment.focuser?.connected}
            data={equipment.focuser}
            stale={staleDevices.focuser}
            lastReportedControl={renderLastReportedControl('focuser')}
            equipmentName={equipment.focuser?.name}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Position:</Typography>
                <Typography variant="body2">{equipment.focuser?.position}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Temperature:</Typography>
                <Typography variant="body2">
                  {equipment.focuser?.temperature !== null && equipment.focuser?.temperature !== undefined ? `${equipment.focuser.temperature}°C` : 'N/A'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Moving:</Typography>
                <Chip
                  label={equipment.focuser?.is_moving ? 'Yes' : 'No'}
                  size="small"
                  color={equipment.focuser?.is_moving ? 'warning' : 'default'}
                />
              </Box>
              {/* Driver Info */}
              {equipment.focuser?.driver_version && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>Driver</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Version:</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{equipment.focuser.driver_version}</Typography>
                  </Box>
                </>
              )}
            </Box>
          </EquipmentStatusCard>
        </Box>}

        {/* Rotator */}
        {isEquipmentVisible('rotator') && <Box sx={{ width: 512, maxWidth: '100%' }}>
          <EquipmentStatusCard
            title="Rotator"
            icon={<RotatorIcon color="primary" />}
            connected={equipment.rotator?.connected}
            data={equipment.rotator}
            stale={staleDevices.rotator}
            lastReportedControl={renderLastReportedControl('rotator')}
            equipmentName={equipment.rotator?.name}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Position:</Typography>
                <Typography variant="body2">{equipment.rotator?.position?.toFixed(1)}°</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Mechanical:</Typography>
                <Typography variant="body2">{equipment.rotator?.mechanical_position?.toFixed(1)}°</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Moving:</Typography>
                <Chip
                  label={equipment.rotator?.is_moving ? 'Yes' : 'No'}
                  size="small"
                  color={equipment.rotator?.is_moving ? 'warning' : 'default'}
                />
              </Box>
              {/* Driver Info */}
              {equipment.rotator?.driver_version && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>Driver</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Version:</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{equipment.rotator.driver_version}</Typography>
                  </Box>
                </>
              )}
            </Box>
          </EquipmentStatusCard>
        </Box>}

        {/* Dome */}
        {isEquipmentVisible('dome') && <Box sx={{ width: 512, maxWidth: '100%' }}>
          <EquipmentStatusCard
            title="Dome"
            icon={<DomeIcon color="primary" />}
            connected={equipment.dome?.connected}
            data={equipment.dome}
            stale={staleDevices.dome}
            lastReportedControl={renderLastReportedControl('dome')}
            equipmentName={equipment.dome?.name}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {typeof equipment.dome?.azimuth === 'number' && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Azimuth:</Typography>
                <Typography variant="body2">{equipment.dome.azimuth.toFixed(1)}°</Typography>
              </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Shutter:</Typography>
                <Typography variant="body2">{equipment.dome?.shutter_status || 'Unknown'}</Typography>
              </Box>
              {(equipment.dome?.at_park || equipment.dome?.at_home || equipment.dome?.slewing) && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Status:</Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {equipment.dome?.at_park && <Chip label="Parked" size="small" />}
                  {equipment.dome?.at_home && <Chip label="Home" size="small" />}
                  {equipment.dome?.slewing && <Chip label="Slewing" size="small" color="warning" />}
                </Box>
              </Box>
              )}
              {/* Driver Info */}
              {equipment.dome?.driver_version && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>Driver</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Version:</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{equipment.dome.driver_version}</Typography>
                  </Box>
                </>
              )}
            </Box>
          </EquipmentStatusCard>
        </Box>}

        {/* Weather */}
        {isEquipmentVisible('weather_station') && <Box sx={{ width: 512, maxWidth: '100%' }}>
          <EquipmentStatusCard
            title="Weather Station"
            icon={<WeatherIcon color="primary" />}
            connected={equipment.weather?.connected}
            data={equipment.weather}
            stale={staleDevices.weather}
            lastReportedControl={renderLastReportedControl('weather')}
            equipmentName={equipment.weather?.name}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {equipment.weather?.temperature != null && !isNaN(equipment.weather.temperature) && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Temperature:</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {Number(equipment.weather.temperature).toFixed(1)}°C / {(Number(equipment.weather.temperature) * 9/5 + 32).toFixed(1)}°F
                  </Typography>
                </Box>
              )}
              {equipment.weather?.humidity != null && !isNaN(equipment.weather.humidity) && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Humidity:</Typography>
                  <Typography variant="body2">{Number(equipment.weather.humidity).toFixed(0)}%</Typography>
                </Box>
              )}
              {equipment.weather?.dewpoint != null && !isNaN(equipment.weather.dewpoint) && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Dew Point:</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {Number(equipment.weather.dewpoint).toFixed(1)}°C / {(Number(equipment.weather.dewpoint) * 9/5 + 32).toFixed(1)}°F
                  </Typography>
                </Box>
              )}
              {equipment.weather?.wind_speed != null && !isNaN(equipment.weather.wind_speed) && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Wind Speed:</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {Number(equipment.weather.wind_speed).toFixed(1)} m/s / {(Number(equipment.weather.wind_speed) * 2.237).toFixed(1)} mph
                  </Typography>
                </Box>
              )}
              {equipment.weather?.wind_direction !== undefined && equipment.weather?.wind_direction !== null && !isNaN(equipment.weather.wind_direction) && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Wind Direction:</Typography>
                  <Typography variant="body2">{Number(equipment.weather.wind_direction).toFixed(0)}°</Typography>
                </Box>
              )}
              {equipment.weather?.pressure !== undefined && equipment.weather?.pressure !== null && !isNaN(equipment.weather.pressure) && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Pressure:</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {Number(equipment.weather.pressure).toFixed(0)} hPa / {(Number(equipment.weather.pressure) * 0.02953).toFixed(2)} inHg
                  </Typography>
                </Box>
              )}
              {equipment.weather?.rain_rate !== undefined && equipment.weather?.rain_rate !== null && !isNaN(equipment.weather.rain_rate) && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Rain Rate:</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {Number(equipment.weather.rain_rate).toFixed(1)} mm/hr / {(Number(equipment.weather.rain_rate) * 0.0394).toFixed(2)} in/hr
                  </Typography>
                </Box>
              )}
              {equipment.weather?.cloud_cover !== undefined && equipment.weather?.cloud_cover !== null && !isNaN(equipment.weather.cloud_cover) && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Cloud Cover:</Typography>
                  <Typography variant="body2">{Number(equipment.weather.cloud_cover).toFixed(0)}%</Typography>
                </Box>
              )}
              {equipment.weather?.sky_quality !== undefined && equipment.weather?.sky_quality !== null && !isNaN(equipment.weather.sky_quality) && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Sky Quality:</Typography>
                  <Typography variant="body2">{Number(equipment.weather.sky_quality).toFixed(2)} mag/arcsec²</Typography>
                </Box>
              )}
              {equipment.weather?.sky_temperature !== undefined && equipment.weather?.sky_temperature !== null && !isNaN(equipment.weather.sky_temperature) && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Sky Temperature:</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {Number(equipment.weather.sky_temperature).toFixed(1)}°C / {(Number(equipment.weather.sky_temperature) * 9/5 + 32).toFixed(1)}°F
                  </Typography>
                </Box>
              )}
              {/* Driver Info */}
              {equipment.weather?.driver_version && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>Driver</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Version:</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{equipment.weather.driver_version}</Typography>
                  </Box>
                </>
              )}
            </Box>
          </EquipmentStatusCard>
        </Box>}

        {/* Guider */}
        {isEquipmentVisible('guider') && <Box sx={{ width: 512, maxWidth: '100%' }}>
          <EquipmentStatusCard
            title="Guider"
            icon={<GuiderIcon color="primary" />}
            connected={equipment.guider?.connected}
            data={equipment.guider}
            equipmentName={equipment.guider?.name}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Guiding:</Typography>
                <Chip 
                  label={equipment.guider?.guiding ? 'Active' : 'Inactive'} 
                  size="small"
                  color={equipment.guider?.guiding ? 'success' : 'default'}
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">RMS Total:</Typography>
                <Typography variant="body2">{equipment.guider?.rms_total?.toFixed(2)}"</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">RMS RA/Dec:</Typography>
                <Typography variant="body2">
                  {equipment.guider?.rms_ra?.toFixed(2)}" / {equipment.guider?.rms_dec?.toFixed(2)}"
                </Typography>
              </Box>
              {/* Driver Info */}
              {equipment.guider?.driver_version && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>Driver</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Version:</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{equipment.guider.driver_version}</Typography>
                  </Box>
                </>
              )}
            </Box>
          </EquipmentStatusCard>
        </Box>}

        {/* Live guider RMS plot (issue #673). Rendered as its own card directly
            below the Guider status card so users can see live performance
            during a guiding session. Hidden when no guider is connected. */}
        {isEquipmentVisible('guider') && equipment.guider?.connected && (
          <Box sx={{ width: 512, maxWidth: '100%' }}>
            <GuiderChart guiderHistory={guiderHistory} guider={equipment.guider} />
          </Box>
        )}

        {/* Safety Monitor */}
        {isEquipmentVisible('safety_monitor') && <Box sx={{ width: 512, maxWidth: '100%' }}>
          <EquipmentStatusCard
            title="Safety Monitor"
            icon={<SafetyMonitorIcon color="primary" />}
            connected={equipment.safetyMonitor?.connected}
            data={equipment.safetyMonitor}
            stale={staleDevices.safetyMonitor}
            lastReportedControl={renderLastReportedControl('safetyMonitor')}
            equipmentName={equipment.safetyMonitor?.name}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Status:</Typography>
                <Chip
                  label={equipment.safetyMonitor?.is_safe ? 'SAFE' : 'UNSAFE'}
                  size="small"
                  color={equipment.safetyMonitor?.is_safe ? 'success' : 'error'}
                />
              </Box>
              {/* Driver Info */}
              {equipment.safetyMonitor?.driver_version && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>Driver</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Version:</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{equipment.safetyMonitor.driver_version}</Typography>
                  </Box>
                </>
              )}
            </Box>
          </EquipmentStatusCard>
        </Box>}

        {/* Switches */}
        {isEquipmentVisible('switch_hub') && <Box sx={{ width: 512, maxWidth: '100%' }}>
          <EquipmentStatusCard
            title="Switch Hub"
            icon={<SwitchIcon color="primary" />}
            connected={equipment.switches?.connected}
            data={equipment.switches}
            stale={staleDevices.switches}
            lastReportedControl={renderLastReportedControl('switches')}
            equipmentName={equipment.switches?.name}
          >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {/* Writable switches (controllable) */}
                {equipment.switches?.writable_switches && equipment.switches.writable_switches.length > 0 && (
                  <>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                      Controls
                    </Typography>
                    {equipment.switches.writable_switches.map(sw => (
                      <Box key={sw.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                          {sw.name || sw.description || `Switch ${sw.id}`}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {sw.minimum === 0 && sw.maximum === 1 ? (
                            // Boolean switch
                            <Chip
                              icon={<ToggleIcon />}
                              label={sw.value === 1 ? 'ON' : 'OFF'}
                              size="small"
                              color={sw.value === 1 ? 'success' : 'default'}
                            />
                          ) : (
                            // Numeric value
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {sw.value?.toFixed(1)} / {sw.target_value?.toFixed(1)}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </>
                )}
                
                {/* Readonly switches (sensors) */}
                {equipment.switches?.readonly_switches && equipment.switches.readonly_switches.length > 0 && (
                  <>
                    {equipment.switches?.writable_switches?.length > 0 && <Divider sx={{ my: 1 }} />}
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                      Sensors
                    </Typography>
                    {equipment.switches.readonly_switches.map(sw => (
                      <Box key={sw.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                          {sw.name || sw.description || `Sensor ${sw.id}`}
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {sw.value !== undefined && sw.value !== null ? sw.value.toFixed(1) : 'N/A'}
                        </Typography>
                      </Box>
                    ))}
                  </>
                )}
                
                {/* If no switches are configured */}
                {(!equipment.switches?.writable_switches || equipment.switches.writable_switches.length === 0) &&
                 (!equipment.switches?.readonly_switches || equipment.switches.readonly_switches.length === 0) && (
                  <Typography variant="body2" color="text.secondary">
                    No switches configured
                  </Typography>
                )}
                {/* Driver Info */}
                {equipment.switches?.driver_version && (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>Driver</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">Version:</Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{equipment.switches.driver_version}</Typography>
                    </Box>
                  </>
                )}
              </Box>
          </EquipmentStatusCard>
        </Box>}

        {/* Flat Device */}
        {isEquipmentVisible('flat_device') && <Box sx={{ width: 512, maxWidth: '100%' }}>
          <EquipmentStatusCard
            title="Flat Device"
            icon={<FlatPanelIcon color="primary" />}
            connected={equipment.flatDevice?.connected}
            data={equipment.flatDevice}
            stale={staleDevices.flatDevice}
            lastReportedControl={renderLastReportedControl('flatDevice')}
            equipmentName={equipment.flatDevice?.name}
          >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Light:</Typography>
                  <Chip
                    label={equipment.flatDevice?.light_on ? 'ON' : 'OFF'}
                    size="small"
                    color={equipment.flatDevice?.light_on ? 'warning' : 'default'}
                  />
                </Box>
                {equipment.flatDevice?.brightness !== undefined && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Brightness:</Typography>
                    <Typography variant="body2">
                      {equipment.flatDevice.brightness}% ({equipment.flatDevice.min_brightness}-{equipment.flatDevice.max_brightness})
                    </Typography>
                  </Box>
                )}
                {equipment.flatDevice?.cover_state && equipment.flatDevice.cover_state !== 'NotPresent' && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Cover:</Typography>
                    <Typography variant="body2">
                      {equipment.flatDevice.cover_state}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Capabilities:</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {equipment.flatDevice?.supports_on_off && <Chip label="Light" size="small" variant="outlined" />}
                    {equipment.flatDevice?.supports_open_close && <Chip label="Cover" size="small" variant="outlined" />}
                  </Box>
                </Box>
                {/* Driver Info */}
                {equipment.flatDevice?.driver_version && (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>Driver</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">Version:</Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{equipment.flatDevice.driver_version}</Typography>
                    </Box>
                  </>
                )}
              </Box>
          </EquipmentStatusCard>
        </Box>}
      </Box>
              </>
            );
          },

          system_status: () => {
            if (!isSectionVisible('system_status')) return null;
            return (
      <>
      <Typography variant="h5" sx={{ mb: 2 }}>System Status</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        {/* System Resources */}
        <Box sx={{ flex: '1 1 400px', maxWidth: '100%' }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>System Resources</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2">CPU Usage</Typography>
                    <Typography variant="body2">{system.cpu_usage?.toFixed(0)}%</Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={system.cpu_usage || 0} 
                    color={system.cpu_usage > 80 ? 'error' : 'primary'}
                  />
                </Box>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2">Memory Usage</Typography>
                    <Typography variant="body2">{system.memory_usage?.toFixed(0)}%</Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={system.memory_usage || 0}
                    color={system.memory_usage > 80 ? 'error' : 'primary'}
                  />
                </Box>
                
                {/* Storage Utilization */}
                {(system.disk_space?.main_drive || system.disk_space?.image_drive) && (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    <Typography variant="subtitle2" sx={{ mt: 0.5, mb: 1 }}>
                      Storage Utilization
                    </Typography>
                    {system.disk_space?.main_drive && (
                      <Box sx={{ ml: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2">
                            Main Drive {system.disk_space.main_drive.drive_letter ? `(${system.disk_space.main_drive.drive_letter})` : ''}
                          </Typography>
                          <Typography variant="body2">
                            {system.disk_space.main_drive.free_gb?.toFixed(1)} GB free of {system.disk_space.main_drive.total_gb?.toFixed(1)} GB
                          </Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={system.disk_space.main_drive.used_percent || 0}
                          color={system.disk_space.main_drive.used_percent > 90 ? 'error' : 'primary'}
                        />
                      </Box>
                    )}
                    {system.disk_space?.image_drive && (
                      <Box sx={{ ml: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2">
                            Image Drive {system.disk_space.image_drive.drive_letter ? `(${system.disk_space.image_drive.drive_letter})` : ''}
                          </Typography>
                          <Typography variant="body2">
                            {system.disk_space.image_drive.free_gb?.toFixed(1)} GB free of {system.disk_space.image_drive.total_gb?.toFixed(1)} GB
                          </Typography>
                        </Box>
                        {system.disk_space.image_drive.path && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            {system.disk_space.image_drive.path}
                          </Typography>
                        )}
                        <LinearProgress 
                          variant="determinate" 
                          value={system.disk_space.image_drive.used_percent || 0}
                          color={system.disk_space.image_drive.used_percent > 90 ? 'error' : 'primary'}
                        />
                      </Box>
                    )}
                  </>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* System Information */}
        <Box sx={{ flex: '1 1 400px', maxWidth: '100%' }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>System Information</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {/* Operating System Information */}
                {system.operating_system?.machine_name && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Machine Name:</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {system.operating_system.machine_name}
                    </Typography>
                  </Box>
                )}
                {system.operating_system?.os_description && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Operating System:</Typography>
                    <Tooltip title={system.operating_system.os_description} arrow>
                      <Typography variant="body2" sx={{ 
                        fontFamily: 'monospace',
                        fontSize: '0.85rem',
                        textAlign: 'right',
                        maxWidth: '250px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        cursor: 'help'
                      }}>
                        {system.operating_system.os_description}
                      </Typography>
                    </Tooltip>
                  </Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">NINA Version:</Typography>
                  <Typography variant="body2">{system.nina_version || 'Unknown'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">
                    {system.plugins && Array.isArray(system.plugins) ? 'Plugins:' : 'Plugin Version:'}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                    {system.plugins && Array.isArray(system.plugins) ? (
                      <Box sx={{ 
                        display: 'grid',
                        gridTemplateColumns: 'auto auto auto',
                        gap: '4px',
                        alignItems: 'center',
                        fontFamily: 'monospace'
                      }}>
                        {system.plugins.flatMap((plugin, index) => [
                          <Typography key={`name-${index}`} variant="body2" sx={{ 
                            textAlign: 'right',
                            whiteSpace: 'nowrap'
                          }}>
                            {plugin.name || 'Unknown'}
                          </Typography>,
                          <Typography key={`colon-${index}`} variant="body2" sx={{ 
                            textAlign: 'center',
                            width: '8px'
                          }}>
                            :
                          </Typography>,
                          <Typography key={`version-${index}`} variant="body2" sx={{ 
                            textAlign: 'left'
                          }}>
                            {plugin.version || 'Unknown'}
                          </Typography>
                        ])}
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {system.plugin_version || 'Unknown'}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>
      </>
            );
          },

          observatory_tabs: () => {
            // Build filtered tab list from the 4 sections
            const lat = observatory.location?.latitude;
            const lng = observatory.location?.longitude;
            const hasLocation = lat != null && lng != null && !(lat === 0 && lng === 0);

            const allTabs = [
              { key: 'observatory_history', label: 'Observatory History', available: true },
              // weather_history & planetarium_view are opt-in (DEFAULT_VISIBILITY
              // hides them for all roles) — default to hidden when unset.
              { key: 'weather_history', label: 'Weather History', available: true, defaultVisible: false },
              { key: 'latest_image', label: 'Latest Image', available: !!latestImage },
              { key: 'planetarium_view', label: 'Planetarium View', available: hasLocation, defaultVisible: false },
            ];

            const visibleTabs = allTabs.filter(t => t.available && isSectionVisible(t.key, t.defaultVisible ?? true));
            if (visibleTabs.length === 0) return null;

            const clampedIndex = Math.min(observatoryTabIndex, visibleTabs.length - 1);
            const activeKey = visibleTabs[clampedIndex]?.key;

            return (
      <>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={clampedIndex}
            onChange={(e, newVal) => setObservatoryTabIndex(newVal)}
            variant="scrollable"
            scrollButtons="auto"
          >
            {visibleTabs.map(t => (
              <Tab key={t.key} label={t.label} />
            ))}
          </Tabs>
        </Box>
        <Box sx={{ pt: 2 }}>
          {activeKey === 'observatory_history' && (
            <Card>
              <CardContent>
                {/* Actions: open the full paginated/exportable history page in
                    a new tab, or export the complete history as CSV. */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, mb: 1 }}>
                  <Tooltip title="Export history as CSV" arrow>
                    <span>
                      <IconButton size="small" onClick={handleExportHistoryCsv} disabled={exportingHistory}>
                        {exportingHistory ? <CircularProgress size={16} /> : <ExportIcon fontSize="small" />}
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Open full history in new tab" arrow>
                    <IconButton
                      size="small"
                      component="a"
                      href={mode === 'admin' ? `/admin/observatories/${id}/history` : `/observatories/${id}/history`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                {recentHistory && recentHistory.length > 0 ? (() => {
                  // Page against the true total (totalHistoryCount), not just
                  // what's loaded — loadMoreHistory() pulls additional batches
                  // as the user pages beyond the loaded set.
                  const totalPages = Math.max(1, Math.ceil(totalHistoryCount / historyRowsPerPage));
                  const safePage = Math.min(historyPage, totalPages - 1);
                  const pagedHistory = recentHistory.slice(
                    safePage * historyRowsPerPage,
                    safePage * historyRowsPerPage + historyRowsPerPage
                  );
                  const awaitingBatch = pagedHistory.length === 0 &&
                    (loadingMoreHistory || recentHistory.length < totalHistoryCount);
                  return (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {awaitingBatch && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={24} />
                      </Box>
                    )}
                    {pagedHistory.map((event, index) => (
                      <Box key={event._id || index} sx={{
                        display: 'flex',
                        alignItems: 'center',
                        px: 2,
                        py: 0.75,
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                        bgcolor: index % 2 === 0 ? 'background.paper' : 'action.hover',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.selected' }
                      }}
                      onClick={() => handleHistoryEventClick(event)}
                      >
                        <Box sx={{ width: 140, flexShrink: 0 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                            {format(parseISO(event.timestamp), 'MMM dd, HH:mm')}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, width: 200 }}>
                          <Chip
                            label={formatEventType(event.event_type)}
                            size="small"
                            color={getEventColor(event.event_type)}
                          />
                        </Box>
                        <Tooltip title={event.description} arrow>
                          <Typography variant="body2" sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: 2,
                            ml: 1,
                            cursor: 'help'
                          }}>
                            {event.description}
                          </Typography>
                        </Tooltip>
                      </Box>
                    ))}
                    <TablePagination
                      component="div"
                      count={totalHistoryCount}
                      page={safePage}
                      onPageChange={(_, p) => setHistoryPage(p)}
                      rowsPerPage={historyRowsPerPage}
                      onRowsPerPageChange={(e) => {
                        setHistoryRowsPerPage(parseInt(e.target.value, 10));
                        setHistoryPage(0);
                      }}
                      rowsPerPageOptions={[15, 25, 50, 100]}
                      labelDisplayedRows={({ from, to, count }) =>
                        `${from}-${to} of ${count.toLocaleString()}`
                      }
                    />
                  </Box>
                  );
                })() : (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    No history events recorded for this observatory
                  </Typography>
                )}
              </CardContent>
            </Card>
          )}

          {activeKey === 'weather_history' && (
            <WeatherHistorySection observatoryId={id} />
          )}

          {activeKey === 'latest_image' && (() => {
            const imageHeight = accessLevelData?.page_behavior?.latest_image_height || 200;
            return (
              <Paper sx={{ p: 2 }} elevation={2}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{
                    width: imageHeight,
                    flexShrink: 0,
                    bgcolor: 'grey.900',
                    borderRadius: 1,
                    overflow: 'hidden',
                  }}>
                    <img
                      src={constructMinioUrl(latestImage.preview_url, latestImage.updated_at)}
                      alt={latestImage.object_name || 'Latest capture'}
                      style={{
                        width: '100%',
                        display: 'block',
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<div style="color: #666; text-align: center;">Preview not available</div>';
                      }}
                    />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 200, display: 'flex', flexDirection: 'column' }}>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', border: 0, py: 0.5, pl: 0, width: 100 }}>Target:</TableCell>
                          <TableCell sx={{ border: 0, py: 0.5 }}>{latestImage.object_name || latestImage.observation_name || 'Unknown'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', border: 0, py: 0.5, pl: 0 }}>User:</TableCell>
                          <TableCell sx={{ border: 0, py: 0.5 }}>{latestImage.submitted_by || 'Unknown'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', border: 0, py: 0.5, pl: 0 }}>Filter:</TableCell>
                          <TableCell sx={{ border: 0, py: 0.5 }}>{latestImage.filter || 'Unknown'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', border: 0, py: 0.5, pl: 0 }}>Exposure:</TableCell>
                          <TableCell sx={{ border: 0, py: 0.5 }}>{latestImage.exposure_time ? `${latestImage.exposure_time}s` : 'Unknown'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', border: 0, py: 0.5, pl: 0 }}>Captured:</TableCell>
                          <TableCell sx={{ border: 0, py: 0.5 }}>{latestImage.timestamp ? formatDistanceToNow(new Date(latestImage.timestamp), { addSuffix: true }) : 'Unknown'}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                    <Box sx={{ mt: 'auto', pt: 2 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          if (latestImage.observation_id) {
                            navigate(`/observations/${latestImage.observation_id}`);
                          }
                        }}
                        disabled={!latestImage.observation_id}
                      >
                        View Observation
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Paper>
            );
          })()}

          {activeKey === 'planetarium_view' && (() => {
            const provider = observatory.branding?.planetarium_provider || 'virtualsky';
            const mountRa = equipment.mount?.ra;
            const mountDec = equipment.mount?.dec;
            const mountConnected = equipment.mount?.connected;
            const hasCoords = mountConnected && mountRa != null && mountDec != null;

            let planetariumUrl;
            if (provider === 'virtualsky') {
              const vsFov = observatory.branding?.planetarium_fov || 60;
              let vsUrl = `/virtualsky/index.html?longitude=${lng}&latitude=${lat}&projection=gnomic&showstarlabels=true&live=true&fov=${vsFov}`;
              if (hasCoords && trackMount) {
                const raDeg = Math.round(mountRa * 15);
                const decDeg = Math.round(mountDec);
                vsUrl += `&ra=${raDeg}&dec=${decDeg}`;
              }
              planetariumUrl = vsUrl;
            } else {
              planetariumUrl = `https://stellarium-web.org/?lat=${lat}&lng=${lng}`;
            }

            const handleIframeLoad = () => {
              if (provider === 'virtualsky' && hasCoords && trackMount) {
                const iframe = planetariumIframeRef.current;
                if (iframe?.contentWindow) {
                  iframe.contentWindow.postMessage({
                    type: 'center-on-target',
                    ra: mountRa * 15,
                    dec: mountDec,
                  }, '*');
                }
              }
            };

            return (
              <Paper sx={{ p: 0, overflow: 'hidden', position: 'relative' }} elevation={2}>
                <iframe
                  ref={provider === 'virtualsky' ? planetariumIframeRef : undefined}
                  src={planetariumUrl}
                  title={`${observatory.name} Planetarium View`}
                  style={{ width: '100%', height: '600px', border: 'none', display: 'block' }}
                  onLoad={handleIframeLoad}
                  allowFullScreen
                />
                {hasCoords && provider === 'virtualsky' && (
                  <Tooltip title={trackMount ? 'Tracking mount (click to stop)' : 'Not tracking mount (click to track)'}>
                    <IconButton
                      onClick={() => setTrackMount(prev => !prev)}
                      sx={{
                        position: 'absolute', top: 50, right: 12,
                        bgcolor: 'rgba(0,0,0,0.7)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        color: trackMount ? '#4caf50' : '#ff4444',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.85)' },
                        zIndex: 2,
                      }}
                      size="small"
                    >
                      {trackMount ? <GpsFixedIcon /> : <GpsOffIcon />}
                    </IconButton>
                  </Tooltip>
                )}
                {hasCoords && provider === 'virtualsky' && (() => {
                  const currentOverlay = overlayMode || (observatory.branding?.planetarium_overlay || 'reticle');
                  const isReticle = currentOverlay === 'reticle';
                  return (
                    <Tooltip title={isReticle ? 'Showing reticle (click for camera FOV)' : 'Showing camera FOV (click for reticle)'}>
                      <IconButton
                        onClick={() => setOverlayMode(prev => {
                          const current = prev || (observatory.branding?.planetarium_overlay || 'reticle');
                          return current === 'reticle' ? 'fov' : 'reticle';
                        })}
                        sx={{
                          position: 'absolute', top: 88, right: 12,
                          bgcolor: 'rgba(0,0,0,0.7)',
                          border: '1px solid rgba(255,255,255,0.2)',
                          color: 'rgba(255,255,255,0.7)',
                          '&:hover': { bgcolor: 'rgba(0,0,0,0.85)', color: '#fff' },
                          zIndex: 2,
                        }}
                        size="small"
                      >
                        {isReticle ? <ReticleIcon /> : <CropFreeIcon />}
                      </IconButton>
                    </Tooltip>
                  );
                })()}
                {provider === 'virtualsky' && (
                  <Tooltip title="Open in new tab">
                    <IconButton
                      onClick={() => window.open(planetariumUrl + '&observatory_id=' + encodeURIComponent(observatory._id), '_blank')}
                      sx={{
                        position: 'absolute', top: 126, right: 12,
                        bgcolor: 'rgba(0,0,0,0.7)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        color: 'rgba(255,255,255,0.7)',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.85)', color: '#fff' },
                        zIndex: 2,
                      }}
                      size="small"
                    >
                      <OpenInNewIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {trackMount && hasCoords && provider === 'virtualsky' && (() => {
                  const overlayType = overlayMode || (observatory.branding?.planetarium_overlay || 'reticle');
                  if (overlayType === 'none') return null;
                  if (overlayType === 'fov') {
                    const pixelSize = equipment.camera?.pixel_size;
                    const sensorW = equipment.camera?.sensor_width;
                    const sensorH = equipment.camera?.sensor_height;
                    const focalLength = equipment.mount?.focal_length_mm || ninaOptions?.equipment?.telescope?.focal_length_mm || observatory?.configuration?.telescope_config?.focal_length_mm;
                    if (pixelSize && sensorW && sensorH && focalLength) {
                      const fovWDeg = (sensorW * pixelSize) / (focalLength * 1000) * (180 / Math.PI);
                      const fovHDeg = (sensorH * pixelSize) / (focalLength * 1000) * (180 / Math.PI);
                      const vsFov = observatory.branding?.planetarium_fov || 60;
                      const pctW = (fovWDeg / vsFov) * 100;
                      const pctH = (fovHDeg / vsFov) * 100;
                      return (
                        <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Box sx={{
                            width: `${Math.min(pctW, 90)}%`, height: `${Math.min(pctH, 90)}%`,
                            minWidth: 40, minHeight: 28,
                            border: '1.5px solid rgba(255,68,68,0.7)',
                            borderRadius: '2px',
                            position: 'relative',
                          }}>
                            <Typography sx={{ position: 'absolute', bottom: -18, left: '50%', transform: 'translateX(-50%)', color: 'rgba(255,68,68,0.8)', fontSize: '0.6rem', whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
                              {fovWDeg.toFixed(1)}° x {fovHDeg.toFixed(1)}°
                            </Typography>
                          </Box>
                        </Box>
                      );
                    }
                    return (
                      <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Box sx={{
                          minWidth: 60, minHeight: 40,
                          border: '1.5px dashed rgba(255,68,68,0.5)',
                          borderRadius: '2px',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <Typography sx={{ color: 'rgba(255,68,68,0.6)', fontSize: '0.55rem', fontFamily: 'monospace' }}>
                            No camera data
                          </Typography>
                        </Box>
                      </Box>
                    );
                  }
                  return (
                    <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width="40" height="40" viewBox="0 0 40 40" style={{ opacity: 0.8 }}>
                        <line x1="20" y1="4" x2="20" y2="16" stroke="#ff4444" strokeWidth="1.5" />
                        <line x1="20" y1="24" x2="20" y2="36" stroke="#ff4444" strokeWidth="1.5" />
                        <line x1="4" y1="20" x2="16" y2="20" stroke="#ff4444" strokeWidth="1.5" />
                        <line x1="24" y1="20" x2="36" y2="20" stroke="#ff4444" strokeWidth="1.5" />
                        <circle cx="20" cy="20" r="8" fill="none" stroke="#ff4444" strokeWidth="1" />
                      </svg>
                    </Box>
                  );
                })()}
                {trackMount && hasCoords && (
                  <Box sx={{
                    position: 'absolute', top: 12, left: 12,
                    bgcolor: 'rgba(0,0,0,0.7)', borderRadius: 1, px: 1.5, py: 0.75,
                    display: 'flex', flexDirection: 'column', gap: 0.25,
                    border: '1px solid rgba(255,255,255,0.2)',
                    pointerEvents: 'none',
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Typography variant="caption" sx={{ color: '#ff4444', fontWeight: 600, fontSize: '0.65rem', letterSpacing: 1, textTransform: 'uppercase' }}>
                        Mount:
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#fff', fontFamily: 'monospace', fontSize: '0.7rem', textTransform: 'uppercase' }}>
                        {equipment.mount?.state || (equipment.mount?.tracking ? 'TRACKING' : equipment.mount?.parked ? 'PARKED' : equipment.mount?.slewing ? 'SLEWING' : 'IDLE')}
                      </Typography>
                    </Box>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontFamily: 'monospace', fontSize: '0.65rem' }}>
                      Alt {equipment.mount?.altitude?.toFixed(1) ?? '--'}° Az {equipment.mount?.azimuth?.toFixed(1) ?? '--'}°
                    </Typography>
                  </Box>
                )}
              </Paper>
            );
          })()}
        </Box>
      </>
            );
          },

          clear_sky_chart: () => {
            if (!isSectionVisible('clear_sky_chart') || !observatory.branding?.clear_sky_chart_img) return null;
            return (
      <>
        <Typography variant="h5" sx={{ mb: 2 }}>Clear Sky Chart</Typography>
        <Paper sx={{ p: 2, textAlign: 'center', maxWidth: 1488 }} elevation={2}>
          <a
            href={observatory.branding.clear_sky_chart_url || '#'}
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src={observatory.branding.clear_sky_chart_img}
              alt={`${observatory.name} Clear Sky Chart`}
              style={{ maxWidth: '100%' }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </a>
        </Paper>
      </>
            );
          },

          live_feeds: () => {
            if (!isSectionVisible('live_feeds') || !observatory.branding?.live_feed_url) return null;
            return (
      <>
        <Typography variant="h5" sx={{ mb: 2 }}>Live Observatory Feed</Typography>
        <Paper sx={{ p: 0, overflow: 'hidden' }} elevation={2}>
          <iframe
            src={observatory.branding.live_feed_url}
            title={`${observatory.name} Live Feed`}
            style={{ width: '100%', height: `${observatory.branding.live_feed_height || 480}px`, border: 'none' }}
            allowFullScreen
          />
        </Paper>
      </>
            );
          },

          navigation_buttons: () => {
            if (!isSectionVisible('navigation_buttons')) return null;
            return (
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          onClick={() => navigate(`/admin/observations?observatory=${id}`)}
        >
          View Observations
        </Button>
        <Button
          variant="outlined"
          onClick={() => navigate(`/admin/analytics?observatory=${id}`)}
        >
          View Analytics
        </Button>
        <Button
          variant="outlined"
          onClick={() => navigate(`/admin/observatories/${id}/sessions`)}
        >
          View Sessions
        </Button>
      </Box>
            );
          },
        };

        // Define section pairs that should render side-by-side when both are visible
        const SECTION_PAIRS = [];
        const pairedContent = {};
        const activePairs = {};
        const pairedAlready = new Set();
        for (const pair of SECTION_PAIRS) {
          const contents = {};
          for (const k of pair) {
            const renderer = sectionRenderers[k];
            if (renderer) contents[k] = renderer();
          }
          if (pair.every(k => contents[k])) {
            for (const k of pair) {
              pairedContent[k] = contents[k];
              activePairs[k] = pair;
            }
          }
        }

        // Render sections in order with dividers between visible ones
        const renderedSections = [];
        for (const key of effectiveOrder) {
          if (pairedAlready.has(key)) continue;

          // When we hit a paired section, render both side by side
          if (activePairs[key]) {
            const pair = activePairs[key];
            for (const k of pair) pairedAlready.add(k);
            if (renderedSections.length > 0) {
              renderedSections.push(<Divider key={`divider-${key}`} sx={{ my: 4 }} />);
            }
            const orderedPair = pair.slice().sort(
              (a, b) => effectiveOrder.indexOf(a) - effectiveOrder.indexOf(b)
            );
            renderedSections.push(
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }} key={`pair-${pair.join('-')}`}>
                {orderedPair.map(k => (
                  <Box sx={{ flex: '1 1 400px', maxWidth: '100%' }} key={k}>{pairedContent[k]}</Box>
                ))}
              </Box>
            );
            continue;
          }

          const renderer = sectionRenderers[key];
          if (!renderer) continue;
          const content = renderer();
          if (content) {
            if (renderedSections.length > 0) {
              renderedSections.push(<Divider key={`divider-${key}`} sx={{ my: 4 }} />);
            }
            renderedSections.push(<React.Fragment key={key}>{content}</React.Fragment>);
          }
        }
        return renderedSections;
      })()}

      {/* History Event Details Modal */}
      <Dialog 
        open={historyModalOpen} 
        onClose={handleHistoryModalClose}
        maxWidth="md" 
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">History Event Details</Typography>
          <IconButton onClick={handleHistoryModalClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedHistoryEvent && (
            <TableContainer>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Event Type</TableCell>
                    <TableCell>
                      <Chip
                        label={formatEventType(selectedHistoryEvent.event_type)}
                        color={getEventColor(selectedHistoryEvent.event_type)}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Source</TableCell>
                    <TableCell>
                      <Chip 
                        label={selectedHistoryEvent.source}
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                    <TableCell>{selectedHistoryEvent.description}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Timestamp</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {format(parseISO(selectedHistoryEvent.timestamp), 'PPpp')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDistanceToNow(parseISO(selectedHistoryEvent.timestamp), { addSuffix: true })}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                  {/* Special handling for ownership transfer events */}
                  {selectedHistoryEvent.event_type === 'ownership_transferred' && selectedHistoryEvent.metadata?.transfer_details && (
                    <>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Previous Owner</TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">
                              {selectedHistoryEvent.metadata.transfer_details.previous_owner?.name || 'Unknown'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {selectedHistoryEvent.metadata.transfer_details.previous_owner?.type || 'unknown'}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>New Owner</TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">
                              {selectedHistoryEvent.metadata.transfer_details.new_owner?.name || 'Unknown'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {selectedHistoryEvent.metadata.transfer_details.new_owner?.type || 'unknown'}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Initiated By</TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {selectedHistoryEvent.metadata.transfer_details.initiated_by?.name || selectedHistoryEvent.metadata.transferred_by_name || 'Unknown'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                      {selectedHistoryEvent.metadata.reason && (
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>Transfer Reason</TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {selectedHistoryEvent.metadata.reason}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  )}
                  {selectedHistoryEvent.metadata && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', verticalAlign: 'top' }}>
                        {selectedHistoryEvent.event_type === 'ownership_transferred' ? 'Complete Metadata' : 'Metadata'}
                      </TableCell>
                      <TableCell>
                        {selectedHistoryEvent.event_type === 'ownership_transferred' && (
                          <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                            Technical details including all IDs, timestamps, and transfer information
                          </Typography>
                        )}
                        <Box component="pre" sx={{ 
                          fontFamily: 'monospace', 
                          fontSize: '0.875rem',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          backgroundColor: 'grey.50',
                          color: 'grey.900',
                          border: '1px solid',
                          borderColor: 'grey.300',
                          p: 1,
                          borderRadius: 1,
                          maxHeight: selectedHistoryEvent.event_type === 'ownership_transferred' ? 400 : 300,
                          overflow: 'auto'
                        }}>
                          {JSON.stringify(selectedHistoryEvent.metadata, null, 2)}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleHistoryModalClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* NINA Options Dialog - Draggable */}
      <Dialog
        open={ninaOptionsDialogOpen}
        onClose={() => setNinaOptionsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperComponent={DraggablePaper}
        aria-labelledby="draggable-dialog-title"
      >
        <DialogTitle id="draggable-dialog-title" sx={{ cursor: 'move' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            NINA Options - {ninaOptionsDialogType === 'general' ? 'General' :
                           ninaOptionsDialogType === 'equipment' ? 'Equipment' :
                           ninaOptionsDialogType === 'autofocus' ? 'AutoFocus' :
                           ninaOptionsDialogType === 'dome' ? 'Dome' :
                           ninaOptionsDialogType === 'imaging' ? 'Imaging' :
                           ninaOptionsDialogType === 'platesolving' ? 'Plate Solving' : ''}
            <IconButton onClick={() => setNinaOptionsDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {/* Equipment Options */}
          {ninaOptionsDialogType === 'equipment' && (
            ninaOptions ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Camera Options */}
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Camera Options
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 500 }}>Pixel Size</TableCell>
                          <TableCell>
                            {ninaOptions?.equipment?.camera?.pixel_size_um != null
                              ? `${ninaOptions?.equipment?.camera.pixel_size_um} μm`
                              : '—'}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 500 }}>Bayer Pattern</TableCell>
                          <TableCell>{ninaOptions?.equipment?.camera?.bayer_pattern || '—'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 500 }}>Bit Depth</TableCell>
                          <TableCell>
                            {ninaOptions?.equipment?.camera?.bit_depth != null
                              ? `${ninaOptions?.equipment?.camera.bit_depth}-bit`
                              : '—'}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 500 }}>Bit Scaling</TableCell>
                          <TableCell>
                            <Chip
                              label={ninaOptions?.equipment?.camera?.bit_scaling ? 'Enabled' : 'Disabled'}
                              size="small"
                              color={ninaOptions?.equipment?.camera?.bit_scaling ? 'success' : 'default'}
                            />
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 500 }}>Bulb Mode</TableCell>
                          <TableCell>{ninaOptions?.equipment?.camera?.bulb_mode || '—'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 500 }}>Timeout</TableCell>
                          <TableCell>
                            {ninaOptions?.equipment?.camera?.timeout_seconds != null
                              ? `${ninaOptions?.equipment?.camera.timeout_seconds} seconds`
                              : '—'}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 500 }}>Raw Converter</TableCell>
                          <TableCell>{ninaOptions?.equipment?.camera?.raw_converter || '—'}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                {/* Telescope Options */}
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Telescope Options
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 500 }}>Name</TableCell>
                          <TableCell>{ninaOptions?.equipment?.telescope?.name || '—'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 500 }}>Focal Length</TableCell>
                          <TableCell>
                            {ninaOptions?.equipment?.telescope?.focal_length_mm
                              ? `${ninaOptions?.equipment?.telescope.focal_length_mm} mm`
                              : '—'}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 500 }}>Focal Ratio</TableCell>
                          <TableCell>
                            {ninaOptions?.equipment?.telescope?.focal_ratio
                              ? `f/${ninaOptions?.equipment?.telescope.focal_ratio}`
                              : '—'}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                {/* Mount Options */}
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Mount Options
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 500 }}>Name</TableCell>
                          <TableCell>{ninaOptions?.equipment?.mount?.name || '—'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 500 }}>Settle Time</TableCell>
                          <TableCell>
                            {ninaOptions?.equipment?.mount?.settle_time_seconds != null
                              ? `${ninaOptions?.equipment?.mount.settle_time_seconds} seconds`
                              : '—'}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 500 }}>Location Sync</TableCell>
                          <TableCell>
                            <Chip
                              label={ninaOptions?.equipment?.mount?.no_sync ? 'Disabled' : 'Enabled'}
                              size="small"
                              color={ninaOptions?.equipment?.mount?.no_sync ? 'warning' : 'success'}
                            />
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Box>
            ) : (
              <Alert severity="info">
                No NINA Equipment Options data available. Ensure the plugin is sending telemetry.
              </Alert>
            )
          )}

          {/* General Options */}
          {ninaOptionsDialogType === 'general' && (
            ninaOptions?.general ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* NINA Profile */}
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    NINA Profile
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 500 }}>Profile Name</TableCell>
                          <TableCell>{ninaOptions?.general?.profile_name || '—'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 500 }}>Profile Description</TableCell>
                          <TableCell>{ninaOptions?.general?.profile_description || '—'}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                {/* ASCOM Options */}
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    ASCOM Options
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 500 }}>Log Level</TableCell>
                          <TableCell>{ninaOptions?.general?.log_level || '—'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 500 }}>Device Polling Interval</TableCell>
                          <TableCell>
                            {ninaOptions?.general?.device_polling_interval_seconds != null
                              ? `${ninaOptions?.general.device_polling_interval_seconds} seconds`
                              : '—'}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Box>
            ) : (
              <Alert severity="info">
                General options data is not yet available from the plugin.
              </Alert>
            )
          )}

          {/* AutoFocus Options */}
          {ninaOptionsDialogType === 'autofocus' && (
            ninaOptions?.autofocus ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    AutoFocus Settings
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 500 }}>Method</TableCell>
                          <TableCell>{ninaOptions.autofocus?.auto_focus_method || '—'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 500 }}>Step Size</TableCell>
                          <TableCell>
                            {ninaOptions.autofocus?.step_size != null
                              ? `${ninaOptions.autofocus.step_size} steps`
                              : '—'}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 500 }}>Initial Offset Steps</TableCell>
                          <TableCell>
                            {ninaOptions.autofocus?.initial_offset_steps != null
                              ? `${ninaOptions.autofocus.initial_offset_steps} steps`
                              : '—'}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Box>
            ) : (
              <Alert severity="info">
                AutoFocus options data is not yet available from the plugin.
              </Alert>
            )
          )}

          {/* Dome Options */}
          {ninaOptionsDialogType === 'dome' && (
            ninaOptions?.dome ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Dome Settings
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 500 }}>Dome Sync</TableCell>
                          <TableCell>
                            <Chip
                              label={ninaOptions.dome?.dome_sync_enabled ? 'Enabled' : 'Disabled'}
                              size="small"
                              color={ninaOptions.dome?.dome_sync_enabled ? 'success' : 'default'}
                            />
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 500 }}>Settle Time</TableCell>
                          <TableCell>
                            {ninaOptions.dome?.settle_time_seconds != null
                              ? `${ninaOptions.dome.settle_time_seconds} seconds`
                              : '—'}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                {/* Dome & Mount Geometry */}
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Dome & Mount Geometry
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 500 }}>Mount Type</TableCell>
                          <TableCell>{ninaOptions.dome?.mount_type || '—'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 500 }}>Telescope N/S Position</TableCell>
                          <TableCell>
                            {ninaOptions.dome?.scope_position_north_south_mm != null
                              ? `${ninaOptions.dome.scope_position_north_south_mm} mm`
                              : '—'}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 500 }}>Telescope E/W Position</TableCell>
                          <TableCell>
                            {ninaOptions.dome?.scope_position_east_west_mm != null
                              ? `${ninaOptions.dome.scope_position_east_west_mm} mm`
                              : '—'}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 500 }}>Telescope Up/Down Position</TableCell>
                          <TableCell>
                            {ninaOptions.dome?.scope_position_up_down_mm != null
                              ? `${ninaOptions.dome.scope_position_up_down_mm} mm`
                              : '—'}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 500 }}>Dome Radius</TableCell>
                          <TableCell>
                            {ninaOptions.dome?.dome_radius_mm != null
                              ? `${ninaOptions.dome.dome_radius_mm} mm`
                              : '—'}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 500 }}>GEM Axis Length</TableCell>
                          <TableCell>
                            {ninaOptions.dome?.gem_axis_length_mm != null
                              ? `${ninaOptions.dome.gem_axis_length_mm} mm`
                              : '—'}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 500 }}>Lateral Axis Length</TableCell>
                          <TableCell>
                            {ninaOptions.dome?.lateral_axis_length_mm != null
                              ? `${ninaOptions.dome.lateral_axis_length_mm} mm`
                              : '—'}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 500 }}>Azimuth Tolerance</TableCell>
                          <TableCell>
                            {ninaOptions.dome?.azimuth_tolerance_degrees != null
                              ? `${ninaOptions.dome.azimuth_tolerance_degrees}°`
                              : '—'}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Box>
            ) : (
              <Alert severity="info">
                Dome options data is not yet available from the plugin.
              </Alert>
            )
          )}

          {/* Imaging Options */}
          {ninaOptionsDialogType === 'imaging' && (
            ninaOptions?.imaging ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Imaging Settings
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 500 }}>File Pattern</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85em' }}>
                            {ninaOptions.imaging?.file_pattern || '—'}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 500 }}>File Type</TableCell>
                          <TableCell>{ninaOptions.imaging?.image_file_type || '—'}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Box>
            ) : (
              <Alert severity="info">
                Imaging options data is not yet available from the plugin.
              </Alert>
            )
          )}

          {/* Plate Solving Options */}
          {ninaOptionsDialogType === 'platesolving' && (
            ninaOptions?.plate_solving ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Plate Solving Settings
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 500 }}>Primary Solver</TableCell>
                          <TableCell>{ninaOptions.plate_solving?.plate_solver || '—'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 500 }}>Blind Solver</TableCell>
                          <TableCell>{ninaOptions.plate_solving?.blind_solver || '—'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 500 }}>Search Radius</TableCell>
                          <TableCell>
                            {ninaOptions.plate_solving?.search_radius_degrees != null
                              ? `${ninaOptions.plate_solving.search_radius_degrees}°`
                              : '—'}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Box>
            ) : (
              <Alert severity="info">
                Plate Solving options data is not yet available from the plugin.
              </Alert>
            )
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNinaOptionsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Personalization Dialog */}
      <ObservatoryPersonalizationDialog
        open={personalizationDialogOpen}
        onClose={() => setPersonalizationDialogOpen(false)}
        observatory={observatory}
        onSave={() => {
          loadObservatory(false);
          loadAccessLevel();
        }}
      />

      <CreateMotdDialog
        open={createMotdOpen}
        onClose={() => setCreateMotdOpen(false)}
        observatoryId={id}
        observatoryName={observatory?.name}
        onCreated={() => { loadActiveMotds(); loadRecentHistory(); loadMotdHistory(); }}
      />

      <MotdDialog
        open={motdDialogOpen}
        onClose={() => { setMotdDialogOpen(false); setSelectedMotd(null); }}
        motd={selectedMotd}
        onAcknowledged={handleMotdAcknowledged}
      />

      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        confirmColor={confirm?.confirmColor}
        confirmIcon={confirm?.confirmIcon}
        icon={confirm?.icon}
        onConfirm={confirm?.onConfirm}
        onClose={() => setConfirm(null)}
      />
    </Box>
  );
}

export default ObservatoryDetailsBase;
